/**
 * Per-page chrome: heroes, eyebrows, stat-strip labels, empty states, filter
 * names and category labels.
 *
 * Split from `common` because these strings are *about a page* rather than
 * about the product. Keeping them together means a new page's hero is one
 * import away rather than a hunt through a 2,000-line file.
 *
 * Only chrome lives here. Topic titles, group names, chat messages and
 * captions are user or generated content and stay in whatever language they
 * were created in — translating a learner's own topic title would be editing
 * their words, not localising the app. Category names are the one boundary
 * case: they are a fixed enum stored in English and drive filtering, so only
 * the label is translated and the stored value stays the English slug.
 */

export const pages = {
  topics: {
    title: "Browse Topics",
    description: "Find a topic worth talking about, then practise it out loud.",
    eyebrow: "Explore",
    metaLabel: "Speaking Practice",
    metaDescription: "Practice conversation skills with AI-generated discussion prompts and real-time feedback.",
    communityLabel: "Community Topics",
    communityDescription: "Browse practice topics created and shared by other language learners and teachers.",
    aiLabel: "AI-Powered",
    aiDescription: "Generate new practice topics tailored to your level, interests, and learning goals.",
    activeLearners: "Active learners",
    practisingNow: "practising now",
    createdByYou: "created by you",
    statsLabel: "Topics library statistics",
    libraryTotal: "Topics in the library",
    categoryCount_other: "{{count}} categories",
    myTopics: "Your personal topics",
    pickUpTitle: "Pick up where you left off",
    categoriesLabel: "Categories",
    allCategories: "All",
    searchPlaceholder: "Search topics...",
    createTopic: "Create a Topic",
    clearSearch: "Clear search",
    noQuestionsYet: "No questions yet",
    filterAllTitle: "All Topics",
    emptyAllTitle: "No Topics Yet",
    emptyAllDescription: "Be the first to create a public topic!",
    categoryTitle: "Category: {{name}}",
    noTopicsInCategory: "No Topics in {{name}}",
    noTopicsInCategoryDescription: "Check back later for new topics in this category.",
    showingPage: "Showing page {{page}} of {{totalPages}} ({{total}} total)",
    resultCount: "{{count}} topics",
    practiseNow: "Practise now",
    resultsHeading: "Topic results",
    sortAria: "Sort topics",
    /** The two verbs a topic card offers. */
    practise: "Practise",
    viewTopic: "View",
    filterAria: "Filter topics",
    loadErrorTitle: "Couldn't load topics",
    loadErrorDescription:
      "The library didn't answer. That is usually the connection, not your library — nothing has been lost.",
    loadErrorRetry: "Try again",
    noMatchTitle: "No topics match “{{query}}”",
    noMatchDescription: "Try a different word, or clear the search to see the whole library.",
    emptyCategoryTitle: "Nothing in {{category}} yet",
    emptyCategoryDescription:
      "No one has published here yet. The rest of the library is still waiting.",
    browseAllTopics: "Browse all topics",
    seoAllDescription: "Explore all public speaking practice topics created by the community. Practice with questions and vocabulary to improve your conversation skills.",
    seoCategoryDescription: "Explore speaking practice topics in the {{name}} category. Practice with questions and vocabulary to improve your conversation skills.",
    seoFilterDescription: "Browse {{name}} for speaking practice. Improve your conversation skills with curated topics.",
    categories: {
      General: "General",
      Business: "Business",
      Travel: "Travel",
      Technology: "Technology",
      "Health & Fitness": "Health & Fitness",
      Education: "Education",
      Entertainment: "Entertainment",
      "Daily Life": "Daily Life",
      Lifestyle: "Lifestyle",
      Social: "Social",
      Politics: "Politics",
      History: "History",
      Sports: "Sports",
      "Food & Cooking": "Food & Cooking",
      Finance: "Finance",
      "Art & Design": "Art & Design",
      Environment: "Environment",
    },
    showingTopics_one: "Showing {{count}} topic",
    showingTopics_other: "Showing {{count}} topics",
    categoryCount_one: "{{count}} category",
    filterRecentPracticeTitle: "Recent Practice",
    filterRecentPracticeEmptyTitle: "No Practice Yet",
    filterRecentPracticeEmptyDescription: "Start practicing your topics to see your recent activity here.",
    filterRecentStudiedTitle: "Recently Studied",
    filterRecentStudiedEmptyTitle: "No Study History Yet",
    filterRecentStudiedEmptyDescription: "Start studying topics to track your learning progress.",
    filterBookmarkedTitle: "Bookmarked Topics",
    filterBookmarkedEmptyTitle: "No Bookmarks Yet",
    filterBookmarkedEmptyDescription: "Bookmark topics to easily find them later.",
    filterPinnedTitle: "Pinned Topics",
    filterPinnedEmptyTitle: "No Pinned Topics",
    filterPinnedEmptyDescription: "Pin your favorite topics for quick access.",
    filterRecentTitle: "Recent Topics",
    filterRecentEmptyTitle: "No Topics Yet",
    filterRecentEmptyDescription: "Create your first topic to start practicing.",
    filterTrendingTitle: "Trending Topics",
    filterTrendingEmptyTitle: "No Trending Topics",
    filterTrendingEmptyDescription: "Be the first to create a trending topic!",
    filterPopularTitle: "Most Popular",
    filterPopularEmptyTitle: "No Popular Topics Yet",
    filterPopularEmptyDescription: "Like topics to help them become popular!",
    filterMostBookmarkedTitle: "Most Bookmarked",
    filterMostBookmarkedEmptyTitle: "No Bookmarked Topics Yet",
    filterMostBookmarkedEmptyDescription: "Bookmark topics to save them for later!",
  },
  /**
   * `/vocabulary/practice` — the study desk: the source-mode header, the mode
   * tabs, the shared review bar, and every mode's prompt chrome. Learning
   * content (the word, its definition, examples, phonetics, part of speech,
   * CEFR level) is user/generated data and is deliberately not here.
   */
  vocabularyPractice: {
    seoTitle: "Practice Vocabulary | Lyder Talk",
    seoDescription: "Practice your saved vocabulary with flashcards and writing exercises",
    /** "Nothing to practise" cards — not-found, no-selection and all-caught-up. */
    notFoundTitle: "Vocabulary Not Found",
    noSelectionTitle: "No Vocabulary Selected",
    notFoundDescription: "No vocabulary was selected for practice.",
    goToVocabulary: "Go to Vocabulary",
    caughtUpTitle: "All caught up!",
    caughtUpDescription: "No vocabulary is due for review today. Great job!",
    noVocabularySelected: "No vocabulary selected.",
    /** Header strip and rail chrome. */
    back: "Go back",
    backToVocabulary: "Back to vocabulary",
    viewSession: "View session: {{topic}}",
    viewCollection: "View collection: {{title}}",
    labelTopic: "Topic:",
    labelCollection: "Collection:",
    /** `already saved` counts are prefixed with the collection's saved words. */
    savedOfTotal_one: "{{saved}} of {{total}} words already saved to your vocabulary",
    savedOfTotal_other: "{{saved}} of {{total}} words already saved to your vocabulary",
    /**
     * Practice-source and flashcard-mode labels. `practice/constants.ts` is a
     * module-scope table and cannot call `t()`, so `labelKey` / `descriptionKey`
     * travel with each entry and are translated at the render site.
     */
    modes: {
      due: "Daily Review",
      selected: "Custom Selection",
      session: "Session Practice",
      collection: "Collection Review",
      flashcard: "Flashcards",
      write: "Writing",
      listening: "Listening",
      scramble: "Word Scramble",
      pronunciation: "Pronunciation",
      descriptionFlashcard: "Review cards with spaced repetition",
      descriptionWrite: "Type the answer or fill in the blank",
      descriptionListening: "Hear the word and type what you hear",
      descriptionScramble: "Unscramble the letters to spell the word",
      descriptionPronunciation: "Say the word aloud and check your pronunciation",
      match: "Match",
      descriptionMatch: "Race the clock to pair words with their meanings",
    },
    /** `PracticeToolbar` — the shuffle / audio / keyboard-shortcut popover. */
    toolbar: {
      sortByReviewDate: "Sort by review date",
      shuffleOrder: "Shuffle order",
      stopAudio: "Stop audio",
      playAudio: "Play audio",
      shortcuts: "Shortcuts",
      keyboardShortcuts: "Keyboard Shortcuts",
      flipCard: "Flip Card",
      submit: "Submit",
      hintSkip: "Hint / Skip",
      review: "Review",
      shuffle: "Shuffle",
    },
    /** `PracticeReviewBar` — the spaced-repetition rating bar shared by every mode. */
    review: {
      again: "Again",
      hard: "Hard",
      good: "Good",
      easy: "Easy",
      question: "How well did you remember?",
      reviewed: "Reviewed",
      reviewCount: "{{count}}× reviewed",
    },
    skip: {
      aria: "Skip this word (already known)",
      title: "Skip (S)",
    },
    hint: {
      trigger: "Hint",
      label: "Hint: ",
    },
    /** `VocabularyMatch` — the timed pairing game. */
    match: {
      prompt: "Tap a word, then its meaning",
      wordColumn: "Words",
      meaningColumn: "Meanings",
      time: "Time left",
      lives: "{{count}} lives left",
      pairsProgress: "{{done}} of {{total}} pairs",
      boardCleared: "Board cleared!",
      boardClearedBody: "+{{seconds}}s · {{count}} more pairs",
      deckClearedBody: "That was every word in this session.",
      nextBoard: "Next board",
      seeResults: "See results",
      outOfHearts: "Out of hearts",
      timeUp: "Time's up",
      sessionCleared: "Session cleared",
      gameOverBody: "{{matched}} words matched · {{score}} points",
      bestCombo: "Best combo {{count}}×",
      playAgain: "Play again",
      backToCards: "Back to flashcards",
      progressSaved: "Your progress is saved as you match.",
    },
    /** `PracticeCompleteScreen`. */
    complete: {
      /** The title the page hands the screen when a round finishes. */
      allDone: "All done!",
      title: "Practice Complete",
      correct: "Correct",
      incorrect: "Incorrect",
      skipped: "Skipped",
      /** Lower-case inline form: "5/10 correct · Great". */
      scoreCorrect: "correct",
      perfect: "Perfect",
      great: "Great",
      good: "Good",
      keepGoing: "Keep going",
      bestStreak: "Best streak: {{count}}",
      resultsBreakdown: "Results Breakdown ({{count}})",
      missedWords: "Missed Words ({{count}})",
    },
    /** Section labels above learning content — furniture, so they translate. */
    sections: {
      definition: "Definition",
      example: "Example",
      words: "Words",
    },
    /** `ModeEmptyState` — shown by a mode with no words to study. */
    empty: {
      description: "You need at least some saved vocabulary to start practice.",
      goBack: "Go Back",
    },
    answerLengthAria: "Answer length",
    check: "Check",
    fillInTheBlank: "Fill in the blank",
    typeTheWord: "Type the word",
    typeTheWordYouHeard: "Type the word you heard",
    enterWordPlaceholder: "Enter the word...",
    typeMissingWordPlaceholder: "Type the missing word...",
    letters: "letters",
    tapToReveal: "Tap to reveal",
    clickOrSpaceToReveal: "Click or Space to reveal",
    scramblePrompt: "Unscramble the letters to spell the word",
    scrambleAllPlaced: "All letters placed — tap Check.",
    startsWithHint: 'Starts with "{{letter}}" ({{count}} letters)',
    lettersHint: "{{hint}}... ({{count}} letters)",
    /** `VocabularyListening`. */
    listening: {
      carefully: "Listen carefully",
      playTitle: "Play (Space)",
      playAria: "Play pronunciation",
      audioUnavailable: "Audio isn't available in this browser. Here's the pronunciation hint:",
      replay: "Replay",
      slowPlaybackTitle: "Toggle slow playback",
      slow: "Slow",
      normal: "Normal",
    },
    /** `VocabularyPracticeSidebar` — the word rail and its dropdown twin. */
    sidebar: {
      hidden: "Hidden",
      collapse: "Collapse",
      openList: "Open word list",
    },
    /** Toasts raised by the page's review handler. */
    toasts: {
      reviewed: "Reviewed: {{difficulty}}",
      savedReviewed: "Saved & reviewed: {{difficulty}}",
      nextReview: '"{{word}}" — Next review {{when}}',
    },
  },
  /** `/vocabulary/notes` + the shared note cards (`WordNotes`, `VocabularyNoteCard`). */
  vocabularyNotes: {
    seoTitle: "Notes | Lyder Talk",
    seoDescription: "Your vocabulary notes and personal notes, all in one place",
    heroTitle: "Notes",
    heroDescription: "Notes you took on vocabulary words during practice, plus your own personal notes — all in one place.",
    badgeWordNotes: "Word notes",
    badgeWordNotesDescription: "Notes attached to vocabulary words you studied during practice sessions.",
    badgePersonalNotes: "Personal notes",
    badgePersonalNotesDescription: "Free-form notes — ideas, reminders, or anything you want to remember.",
    metaPersonal: "Personal",
    newNote: "New note",
    createFirstNote: "Create your first note",
    signInTitle: "Sign in to view your notes",
    signInDescription: "Notes are private to your account. Sign in to create and view them.",
    emptyTitle: "No notes yet",
    emptyDescription: "Add notes to vocabulary words during practice, or jot down your own personal notes here.",
    noMatchTitle: "No notes match your search",
    noMatchDescription: "Try a different keyword or clear the filters.",
    searchPlaceholder: "Search notes...",
    filterAria: "Filter notes",
    filterWordNotes: "Word notes",
    filterPersonal: "Personal",
    /** `WordNotes` — shared by the notes page, the dictionary tab and practice. */
    myNotes: "My notes",
    noteCount_one: "{{count}} note",
    noteCount_other: "{{count}} notes",
    unknownWord: "Unknown word",
    addNote: "Add note",
    wordNotesTitle: "Word Notes",
    wordNotesHint: "Look up a word to see notes you took on it during practice",
    addNoteForWord: 'Add a note or mnemonic for "{{word}}".',
    practiceEmptyHint: "Notes are added from the sticky-note icon on a vocabulary word during practice.",
  },
  /** `/vocabulary/collections` list page and its hero / stats / toolbar / card. */
  vocabularyCollections: {
    seoTitle: "Vocabulary Collections | Lyder Talk",
    seoDescription: "Browse shared vocabulary collections created by the community",
    heroTitle: "Vocabulary Collections",
    heroDescription: "Browse and import shared vocabulary sets from the community. Build a curated deck faster by standing on the shoulders of other learners.",
    createCollection: "Create Collection",
    browseTopics: "Browse Topics",
    badgeCuratedSets: "Curated Sets",
    badgeCuratedSetsDescription: "Hand-picked vocabulary collections on topics from travel to business, ready to import in one click.",
    badgeCommunityLibrary: "Community Library",
    badgeCommunityLibraryDescription: "Discover what other learners are studying. Follow creators, import their sets, and personalize them.",
    badgeImport: "Import & Personalize",
    badgeImportDescription: "Bring in any public set, then edit, enrich, and weave it into your own spaced-repetition schedule.",
    libraryLabel: "Library",
    /** `{{number}}` is pre-formatted with the app locale; `count` only drives the plural. */
    librarySets_one: "{{number}} set",
    librarySets_other: "{{number}} sets",
    libraryWords_one: "{{number}} word",
    libraryWords_other: "{{number}} words",
    statsAria: "Collections statistics",
    statsCommunitySets: "Community sets",
    statsWordsToImport: "{{number}} words to import",
    statsMyCollections: "My collections",
    statsCreatedByYou: "created by you",
    statsInProgress: "In progress",
    statsSavingWords: "saving words",
    statsFromTopics: "From topics",
    statsAutoGenerated: "auto-generated",
    toolbar: {
      searchPlaceholder: "Search collections...",
      sortMostWords: "Most Words",
      filterMine: "Mine",
      filterTopics: "From topics",
      filterPublic: "Public",
      filterShared: "Shared with me",
      advancedFilters: "Advanced filters",
      refineByCreator: "Refine by creator",
      creator: "Creator",
      allCreators: "All creators",
      clearCreatorFilter: "Clear creator filter",
      filterOwnershipAria: "Filter by ownership",
      showingOf_one: "Showing {{filtered}} of {{total}} collection",
      showingOf_other: "Showing {{filtered}} of {{total}} collections",
      creatorChip: "Creator: {{name}}",
      clearChip: "Clear {{label}}",
    },
    empty: {
      noMatchTitle: "No matching collections",
      myTitle: "No collections yet",
      communityTitle: "No public collections yet",
      noMatchDescription: "Try a different search term or clear your filters.",
      myDescription: "Create your first vocabulary collection and share it with the world.",
      communityDescription: "Be the first to create a shared vocabulary set.",
      clearFilters: "Clear filters",
      step: "Step {{count}}",
      steps: {
        browse: "Browse",
        browseDescription: "Explore public sets created by the community",
        import: "Import",
        importDescription: "One-click import any set into your deck",
        practice: "Practice",
        practiceDescription: "Spaced repetition locks the words into memory",
        pickTopic: "Pick a topic",
        pickTopicDescription: "Decide what vocabulary you want to curate",
        addWords: "Add words",
        addWordsDescription: "Build your collection with definitions and examples",
      },
    },
    card: {
      wordCount_one: "{{number}} word",
      wordCount_other: "{{number}} words",
      auto: "Auto",
      complete: "Complete",
      fromTopic: "From topic",
      yourProgress: "Your progress",
      progressAria: "Collection progress",
      anonymous: "Anonymous",
      public: "Public",
      private: "Private",
    },
    /** `/vocabulary/collections/:id` — hero, stat strip, toolbar, word rows, share dialog. */
    detail: {
      seoTitle: "Vocabulary Collection | Lyder Talk",
      notFoundTitle: "Collection not found",
      notFoundDescription: "This vocabulary set doesn't exist or has been removed.",
      browseCollections: "Browse Collections",
      /** Hero (`CollectionDetailHero`). */
      backToCollections: "Back to collections",
      descriptionFallback: "A shared vocabulary collection.",
      badgePublicDescription: "Anyone can discover and import this collection.",
      badgePrivateDescription: "Only you can access this collection.",
      badgeWordCountDescription: "Total vocabulary items in this collection.",
      addWords: "Add Words",
      removeImport: "Remove Import",
      byAuthor: "by {{name}}",
      reviewedOfTotal: "{{reviewed}} of {{total}} words reviewed",
      collectionLabel: "Collection",
      hintAddWords: "Add words to start practicing",
      hintSaved: "Saved to your library",
      hintPractice: "Practice to start reviewing",
      shareable: "Shareable",
      /** Stat strip (`CollectionDetailStats`). */
      statsAria: "Collection statistics",
      masteryAria: "Collection mastery",
      wordsInSet: "Words in set",
      leftToReview: "{{count}} left to review",
      inYourLibrary: "In your library",
      percentSaved: "{{percent}}% saved",
      reviewedCount: "{{count}} reviewed",
      savedCount: "{{count}} saved",
      /** Toolbar (`CollectionDetailToolbar`). */
      refineByLevelOrType: "Refine by level or type",
      noFilterableAttributes: "No filterable attributes in this collection.",
      filterUnreviewed: "Unreviewed",
      showingOf_one: "Showing {{filtered}} of {{total}} word",
      showingOf_other: "Showing {{filtered}} of {{total}} words",
      /** Word rows (`CollectionDetailWordRow`). */
      dragToReorder: "Drag to reorder",
      moreDetails: "More details",
      showDetails: "Show details",
      hideDetails: "Hide details",
      /** Empty state (`CollectionDetailEmptyState`). */
      emptyTitle: "This collection is empty",
      emptyOwnerDescription:
        "Add your first words to start building this collection. You can search your library or generate suggestions with AI.",
      emptyOtherDescription: "The owner hasn't added any words to this collection yet. Check back later.",
      addFirstWords: "Add your first words",
      aiSuggestHint: "You can also let AI suggest theme words",
      emptyOtherHint: "Want to build your own? Create a collection in seconds.",
      /** Auto-generated banner + bulk action bar. */
      autoGeneratedFromTopic: "Auto-generated from topic",
      /** Bulk-action bar: the count and this suffix are separate runs. */
      selectedSuffix: "selected",
      viewTopic: "View topic",
      removeFromCollection: "Remove from collection",
      /** Delete confirmation. */
      deleteTitle: "Delete Collection",
      deleteDescription:
        'Are you sure you want to delete "{{title}}"? This action cannot be undone and all words in this collection will be removed.',
      /** Toasts. */
      sharedToGroupDescription: "Collection shared to group.",
      removedFromGroupDescription: "Collection removed from group.",
      importedTitle: "Imported",
      importedDescription: "Vocabulary set has been added to your saved words.",
      failedToImport: "Failed to import vocabulary set.",
      removedFromImports: "Collection removed from your imports.",
      failedToRemoveImport: "Failed to remove import.",
      wordRemoved: "Word removed from collection.",
      failedToRemoveWord: "Failed to remove word.",
      failedToReorder: "Failed to reorder words.",
      bulkRemoved_one: "{{count}} word removed from collection.",
      bulkRemoved_other: "{{count}} words removed from collection.",
      failedToRemoveWords: "Failed to remove words.",
      deletedTitle: "Deleted",
      deletedDescription: '"{{title}}" has been deleted.',
      failedToDeleteCollection: "Failed to delete collection.",
      failedToCopyLink: "Failed to copy link.",
    },
    /** Share-to-study-group dialog (`ShareCollectionDialog`). */
    share: {
      description: 'Add or remove "{{title}}" from your study groups.',
      searchPlaceholder: "Search groups...",
      loadingGroups: "Loading groups...",
      notAMember: "You need to be a member of a study group first.",
      noGroupMatches: 'No groups match "{{search}}".',
      currentlyShared: "Currently shared",
    },
    /** `/vocabulary/collections/new` and `/vocabulary/collections/:id/edit`. */
    creator: {
      seoTitleCreate: "Create Collection | Lyder Talk",
      seoTitleEdit: "Edit Collection | Lyder Talk",
      seoDescriptionCreate:
        "Create a new vocabulary collection by picking saved words or generating new ones inline",
      seoDescriptionEdit: "Edit your vocabulary collection",
      notFoundDescription: "This collection doesn't exist or has been removed.",
      selectWordsTitle: "Select words",
      selectWordsDescription: "Select at least one word for the collection.",
      /** `CreateCollectionHero`. */
      editingChip: "Editing collection",
      newChip: "New collection",
      editTitle: "Edit {{title}}",
      createTitle: "Create a vocabulary collection",
      editDescription: "Update the details and the words that belong in this set.",
      createDescription:
        "Organize your saved words into a themed set. Pick from your library or generate new ones inline.",
      /** Identity form (`CreateCollectionForm` / `BuildWorkspace`). */
      detailsHeading: "Details",
      collectionHeading: "Collection",
      titleLabel: "Title",
      titlePlaceholder: "e.g. Business English, IELTS Prep...",
      titleRequired: "Title is required",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Brief description...",
      visibilityLabel: "Visibility",
      publicHint: "Public collections appear in the community library and can be imported by anyone.",
      privateHint: "Private collections are only visible to you.",
      /** `CreateCollectionToolbar`. */
      searchSelectedPlaceholder: "Search selected words...",
      dragToReorderHint: "drag to reorder",
      /** `CreateCollectionEmptyState`. */
      noWordsSelected: "No words selected yet",
      noWordsSelectedHint:
        'Pick words from your library, or switch to "Add New Words" to generate fresh terms.',
      /** `BuildWorkspace`. */
      wordsSourceAria: "Words source",
      fromMyVocabulary: "From My Vocabulary",
      addNewWords: "Add New Words",
      noSavedWordsYet: "No saved words yet",
      yourDeck: "Your deck",
      /** `CollectionProgressRail` — labels live in a module table, so the key travels with them. */
      phaseAria: "Phase: {{phase}}",
      phases: {
        build: { label: "Build", hint: "Title + words" },
        review: { label: "Review", hint: "Reveal & save" },
      },
      /** `CollectionActionStrip`. */
      addATitle: "Add a title",
      addOneWord: "Add at least one word",
      readyToReview: "Ready to review",
      addAWordToSave: "Add a word in Build to save",
      wordsReady_one: "{{count}} word ready",
      wordsReady_other: "{{count}} words ready",
      backToEditing: "Back to editing",
      reviewCollection: "Review collection",
      updating: "Updating…",
      creating: "Creating…",
      updateCollection: "Update collection",
      createCollection: "Create collection",
      /** `ReviewDeck`. */
      untitledCollection: "Untitled collection",
      yourCollection: "Your collection",
      noWordsYet: "No words yet. Go back to Build and add some.",
      /** `DiscardChangesGuard`. */
      discardTitle: "Discard your new words?",
      discardDescription:
        "Inline-added words aren't saved until you create the collection. Leaving now loses them.",
      discardConfirm: "Discard and leave",
      keepEditing: "Keep editing",
    },
  },
  noteEditor: {
    deleteNote: "Delete Note",
    editNote: "Edit note",
    cancel: "Cancel",
    save: "Save",
    bold: "Bold",
    italic: "Italic",
    underline: "Underline",
    strikethrough: "Strikethrough",
    inlineCode: "Inline Code",
    bulletList: "Bullet List",
    numberedList: "Numbered List",
    /** Ghost text under an empty editor. */
    addNotePlaceholder: "Add a note...",
    /** The amber section header above a saved or in-progress note. */
    noteLabel: "Note",
  },
  vocabularyWords: {
    enrichment: {
      sentences: "Sentences",
      collocations: "Collocations",
      synonyms: "Synonyms",
      antonyms: "Antonyms",
      inDeck: "In your deck",
    },
    detail: {
      previousWord: "Previous word",
      nextWord: "Next word",
      incompleteInfo: "Incomplete info",
    },
    adder: {
      placeholder: "Type a word and press Enter",
    },
    selector: {
      searchPlaceholder: "Search words...",
      level: "Level",
    },
  },
  vocabularyDeck: {
    tabMyWords: "My Words",
    tabCollections: "Collections",
    tabNotes: "Notes",
    stats: {
      ariaLabel: "Vocabulary statistics",
      dueForReview: "Due for review",
      dueReadyHint: "Ready now — spaced repetition is due",
      dueNothingHint: "Nothing due — come back later",
      wordsSaved: "Words saved",
      completeHint: "{{count}} complete",
      reviewedToday: "Reviewed today",
      needsEnrichment: "Needs enrichment",
      enrichmentAria: "Words needing enrichment",
      last7Days: "Last 7 days",
      reviewCount: "{{count}} review",
      reviewCount_other: "{{count}} reviews",
      ofTotal: "{{count}} of {{total}} · {{percent}}%",
    },
    streak: {
      active: "{{count}} day study streak",
      none: "No active streak",
      dayUnit_one: "day",
      dayUnit_other: "days",
      startToday: "Start today",
      streakLabel: "Study streak",
    },
    tiers: {
      masteryProgress: "Mastery progress",
      mastery: "Mastery",
      otherBucket: "Other / manually added",
    },
    /** `VocabularyGarden` — the deck drawn as four beds in growth order. */
    garden: {
      ariaLabel: "Word garden",
      title: "Your garden",
      hint: "Click a bed to filter your deck",
      inBloom: "In bloom",
      bloomCount: "{{count}} of {{total}}",
      bedAria: "{{stage}}: {{count}} words. Filter your deck.",
    },
    empty: {
      ariaLabel: "No saved vocabulary",
      title: "Start your vocabulary deck",
      browseTitle: "Browse topics",
      browseDescription: "Find a conversation topic that interests you",
      saveTitle: "Save words",
      saveDescription: "Star any word to add it to your personal deck",
      reviewTitle: "Review daily",
      reviewDescription: "Spaced repetition locks words into memory",
      cta: "Browse Topics",
      description: "Save words from any practice session and they'll appear here, ready for review with spaced repetition.",
    },
    noResults: {
      title: "No words match your filters",
      description: "Try clearing a filter or searching for something else.",
      clearFilters: "Clear filters",
    },
    aiWork: {
      enriching: "Enriching vocabulary",
      adding: "Adding vocabulary",
      aiWorking: "AI is working",
      words: "words",
      now: "Now",
      ofTotal: "of {{total}} {{unit}}",
      finishingUp: "Finishing up…",
      failed: "{{count}} failed",
      cancelEnrichment: "Cancel enrichment",
      cancelGeneration: "Cancel generation",
      progressLabel: "{{label}}: {{completed}} of {{total}} {{unit}}",
    },
    toolbar: {
      all: "All",
      due: "Due",
      reviewed: "Reviewed",
      noReview: "No review",
      incomplete: "Incomplete",
      searchPlaceholder: "Search words, definitions, examples...",
      clearSearch: "Clear search",
      viewMode: "View mode",
      gridView: "Grid view",
      listView: "List view",
      advancedFilters: "Advanced filters",
      allLevels: "All levels",
      allTypes: "All types",
      clearCalendarDate: "Clear calendar date",
      filterByReviewStatus: "Filter by review status",
      selectAll: "Select all",
      clearSelection: "Clear selection",
      activeFilters: "Active filters",
      clearAll: "Clear all",
      sortBy: "Sort by",
      sortAlpha: "A-Z",
      sortNewest: "Newest",
      sortOldest: "Oldest",
      sortMostPracticed: "Most practiced",
      resultsCount: "{{count}} words",
      lastReviewed: "Last reviewed {{date}}",
      neverReviewed: "Never reviewed",
      removeWord: "Remove {{word}}",
      selected: "{{count}} selected",
      advancedFiltersDescription: "Refine by level, type, or date",
      certLevel: "Cert level",
      wordType: "Word type",
      showingOf_one: "Showing {{filtered}} of {{total}} card",
      showingOf_other: "Showing {{filtered}} of {{total}} cards",
      levelChip: "Level: {{level}}",
      typeChip: "Type: {{type}}",
      calendarFilter: "Showing {{date}}",
      clearChip: "Clear {{label}}",
    },
    unsave: {
      mascotLine: "Lydi wants to make sure you mean to remove this word",
      title: "Remove saved word?",
      /** Continuation of the sentence the bolded word opens. */
      descriptionTail: "will be removed from your saved vocabulary, along with its review progress. You can save it again anytime.",
      remove: "Remove",
      keepIt: "Keep it",
    },
    personalNote: {
      placeholder: "Enter",
      untitled: "Untitled note",
      deleteAria: "Delete note",
      deleteTitle: "Delete note?",
      deleteDescription: "Are you sure you want to delete this note? This action cannot be undone.",
      delete: "Delete",
      cancel: "Cancel",
    },
    /**
     * Tier chip labels. `tier-config.ts` is a module-scope data table and
     * cannot call `t()`, so the label is translated where it is rendered —
     * the key is what the chip asks for, the fallback is today's copy.
     */
    tierLabels: {
      new: "New",
      learning: "Learning",
      reviewing: "Reviewing",
      mastered: "Mastered",
    },
    tierDescriptions: {
      newDescription: "Just discovered",
      learningDescription: "Building memory",
      reviewingDescription: "Strengthening recall",
      masteredDescription: "Long-term memory",
    },
    /** Overflow menu on a vocabulary card. */
    card: {
      viewDetails: "View Details",
      practice: "Practice",
    },
    calendar: {
      activeDays_one: "{{count}} active day this month",
      activeDays_other: "{{count}} active days this month",
      reviewedOnDay_one: "{{count}} word reviewed",
      reviewedOnDay_other: "{{count}} words reviewed",
      dueOnDay_one: "{{count}} due",
      dueOnDay_other: "{{count}} due",
      monthlyReviewed_one: "{{count}} word reviewed this month",
      monthlyReviewed_other: "{{count}} words reviewed this month",
      moreCount: "+{{count}} more",
      noReviews: "No reviews on this day",
      reviewedSection: "Reviewed",
      dueSection: "Due for review",
      reviewedTag: "reviewed",
      dueTag: "due",
      fewer: "Fewer",
      more: "More",
      upcoming: "Upcoming",
    },
    /** The context band above the deck (`DueReviewGroups`), both states. */
    due: {
      waitingAria: "Words waiting to be learned",
      newWordsLabel: "New words",
      neverStudied_one: "word never studied",
      neverStudied_other: "words never studied",
      nothingDue: "Nothing due for review right now",
      shareOfDeck: "of your {{total}} words",
      perSession: "~{{count}} words a session",
      sessionsToClear: "{{count}} to clear",
      minutesToStart: "~{{count}} min to start",
      startLearning: "Start learning",
      reminderAria: "Daily review reminder",
      dailyReview: "Daily review",
      readyForReview_one: "word ready for review",
      readyForReview_other: "words ready for review",
      minutesShort: "~{{count}} min",
      acrossTopics_one: "across {{count}} topic",
      acrossTopics_other: "across {{count}} topics",
      streakDays: "{{count}}-day streak",
      reviewEverything: "Review everything ({{count}})",
      dueCount: "{{count}} due",
      review: "Review",
      showFewer: "Show fewer",
      moreTopics_one: "{{count}} more topic",
      moreTopics_other: "{{count}} more topics",
    },
    addDialog: {
      title: "Add Vocabulary",
      description: "Add words one by one. AI will generate definitions, examples, and more for each word.",
      descriptionImport: "Add words manually, generate definitions with AI, or import from an Excel/Sheets export.",
      readyCount_one: "{{count}} word ready to generate",
      readyCount_other: "{{count}} words ready to generate",
      stop: "Stop",
      generate: "Generate",
    },
    /** Word-definition popover — chrome only; the entry itself stays English. */
    popover: {
      saved: "Saved",
      savedToast: "Word saved!",
      savedDescription: '"{{word}}" has been added to your vocabulary.',
      failedToSave: "Failed to save",
      couldNotSave: "Could not save vocabulary",
      resources: "Resources",
      askCoach: "Ask Coach",
    },
    page: {
      seoTitle: "Vocabulary | Lyder Talk",
      seoDescription: "Practice your saved vocabulary with flashcards and quizzes",
      heroTitle: "My Vocabulary",
      heroDescription: "Build your personal deck and lock words into long-term memory with spaced repetition.",
      badgeFlashcards: "Flashcards",
      badgeFlashcardsDescription: "Tap-to-reveal interactive cards with audio pronunciation and contextual examples.",
      badgeSpaced: "Spaced Repetition",
      badgeSpacedDescription: "Smart review scheduling that adapts to how well you remember each word.",
      badgeEnrichment: "AI Enrichment",
      badgeEnrichmentDescription: "Auto-fills missing translations, examples, and pronunciation using AI.",
      addWord: "Add Word",
      incompleteWords_one: "word with missing info",
      incompleteWords_other: "words with missing info",
      incompleteHint: "AI auto-fills pronunciation, word types, CEFR level and examples",
      fillAll: "Fill All",
      gapContext: "Context sentences",
      gapPronunciation: "Pronunciation",
      gapExamples: "Examples",
      gapCertLevel: "CEFR level",
      gapWordType: "Word type",
      gapRelated: "Related words",
      enrichmentComplete: "Enrichment complete",
      enriched_one: "Updated {{count}} word",
      enriched_other: "Updated {{count}} words",
      failedSuffix: ", {{count}} failed",
      removed: "Removed",
      removedDescription: '"{{word}}" has been removed from your saved vocabulary.',
      failedToRemove: "Failed to remove vocabulary.",
      deleted_one: "Deleted {{count}} word.",
      deleted_other: "Deleted {{count}} words.",
      failedToDeleteSome: "Failed to delete some words.",
      calendarSection: "Calendar",
      showingLoaded: "Showing {{shown}} of {{total}}",
      loadMore: "Load More ({{count}} remaining)",
      selectionWord_one: "word selected",
      selectionWord_other: "words selected",
      deleteWordsTitle: "Delete Vocabulary Words",
      deleteWordsDescription_one: "Are you sure you want to delete {{count}} selected word? This action cannot be undone.",
      deleteWordsDescription_other: "Are you sure you want to delete {{count}} selected words? This action cannot be undone.",
      deleteWordTitle: "Delete Vocabulary Word",
      deleteWordDescription: 'Are you sure you want to delete "{{word}}"? This action cannot be undone.',
    },
  },
  myTopics: {
    questionsChipDescription: "Discussion questions to practice your speaking skills",
    vocabularyChipDescription: "Key words and phrases with definitions and examples",
    speakingGuideChipDescription: "Natural expressions, sentence starters, and grammar tips",
    view: "View",
    shareToGroup: "Share to Group",
    removeBanner: "Remove banner",
    copied: "Copied",
    copyLink: "Copy Link",
    private: "Private",
    public: "Public",
    addBanner: "Add Banner",
    heroTitle: "What will you practice today?",
    heroDescription: "Search your existing topics or create a new one to start practicing.",
    searchPlaceholder: "Search your topics...",
    allCategories: "All Categories",
    clearFilters: "Clear filters",
    quickGenerateTitle: "Quick Generate",
    quickGenerateDescription: "Enter a topic to generate content",
    chatWithAiTitle: "Chat with AI",
    chatWithAiDescription: "Discover topics naturally",
    searchResults: "Search Results",
    recentTopics: "Recent Topics",
    noSearchResults: "No topics found matching your search.",
    noTopicsYet: "No topics yet. Try creating a new one!",
    loading: "Loading...",
    showMore: "Show More",
    pin: "Pin",
    unpin: "Unpin",
    delete: "Delete",
    deleteTopicTitle: "Delete Topic",
    deleteTopicDescription: 'Are you sure you want to delete "{{topic}}"? This action cannot be undone.',
    topicDeleted: "Topic deleted",
    failedToDeleteTopic: "Failed to delete topic",
    signInRequired: "Sign In Required",
    signInToGenerate: "Please sign in to generate practice content",
    failedToCreateTopic: "Failed to create topic. Please try again.",
    streakProtectedByFreeze: "Your streak was protected by a freeze!",
    unfinishedAria: "Topics that did not finish generating",
    unfinishedTopics_one: "1 topic didn't finish generating",
    unfinishedTopics_other: "{{count}} topics didn't finish generating",
    unfinishedSingleDescription: "“{{topic}}” is missing {{items}}.",
    unfinishedManyDescription: "Questions or vocabulary are missing. Start with “{{topic}}”.",
    unfinishedCardQuestions: "No questions",
    unfinishedCardVocabulary: "No vocabulary",
    missingQuestions: "questions",
    missingVocabulary: "vocabulary",
    missingQuestionsAndVocabulary: "questions and vocabulary",
    nothingGeneratedYet: "No content yet",
    finishGenerating: "Finish generating",
    finishLatest: "Finish latest",
    finishCardAction: "Finish",
    finishGeneratingTopic: "Finish generating {{topic}}",
    generatingContent: "Generating content…",
    quickGenerate: {
      placeholder: "e.g. Job Interview, Ordering Coffee...",
      submit: "Quick generate topic",
      introGenerate: "Enter a topic name and generate practice questions, vocabulary, and a speaking guide.",
      introRegenerate: "Choose which content types to regenerate for this topic.",
      generateSubtitle: "Start from a topic — the AI drafts questions, vocabulary and a speaking guide.",
      regenerateSubtitle: "Choose what to rebuild. Anything you leave unchecked keeps its current content.",
      topicLabel: "Topic",
      questionsDescription: "Prompts to practise speaking",
      vocabularyDescription: "Key words with definitions and examples",
      guideDescription: "Natural expressions, starters and grammar",
      selectedCount: "{{count}} of 3 selected",
      generateContent: "Generate Content",
      regenerateContent: "Regenerate Content",
      practiceQuestions: "Practice Questions",
      keyVocabulary: "Key Vocabulary",
      speakingGuide: "Speaking Guide",
      whatYoullGet: "What you'll get",
      selectToRegenerate: "Select what to regenerate",
      generate: "Generate",
      regenerate: "Regenerate",
      generating: "Generating...",
    },
    sidebar: {
      hidePanel: "Hide panel",
      showPanel: "Show panel",
      hidePanelAria: "Hide topics and conversations panel",
      showPanelAria: "Show topics and conversations panel",
      topicsTitle: "Topics",
      newTopic: "New Topic",
      searchTopics: "Search topics...",
      pinned: "Pinned",
      bookmarked: "Bookmarked",
      history: "History",
      noMatchingTopics: "No matching topics",
      noHistoryYet: "No history yet",
      conversationsTitle: "Conversations",
      newChat: "New Chat",
      searchConversations: "Search conversations...",
      noConversationsFound: "No conversations found",
      noConversationsYet: "No conversations yet",
      moreOptions: "More options",
      timeToday: "Today",
      timeYesterday: "Yesterday",
      timeThisWeek: "This week",
      timeThisMonth: "This month",
      timeOlder: "Older",
      deleteConversationTitle: "Delete conversation?",
      deleteConversationDescription: "This action cannot be undone. The conversation will be permanently deleted.",
      cancel: "Cancel",
      conversationUpdated: "Conversation updated",
      conversationDeleted: "Conversation deleted",
      failedToUpdateConversation: "Failed to update conversation",
      failedToDeleteConversation: "Failed to delete conversation",
    },
    ai: {
      newConversation: "New Conversation",
      newChat: "New chat",
      failedToRename: "Failed to rename conversation. Please try again.",
      topicSaved: "Topic saved!",
      failedToCreateTopic: "Failed to create topic. Please try again.",
    },
    progress: {
      docTitle: "Your Progress - My Topics",
      eyebrow: "Practice history",
      title: "Your progress",
      description: "How much you've practised, and how your answers sounded.",
      seoDescription: "Review how much time you've spent practising and replay your recorded speaking answers.",
      timeTab: "Study time",
      recordingsTab: "Recordings",
      timeLabel: "Time tracking",
      timeDescription: "Automatically captures time spent on every practice session, quiz, and review.",
      recordingsLabel: "Recordings",
      recordingsDescription: "Replay every spoken answer with its AI feedback.",
    },
    chat: {
      messagePlaceholder: "Message AI Assistant…",
      addTools: "Add tools and attachments",
      attachFiles: "Attach files",
      documentsFormat: "PDF, TXT, or MD documents",
      assistantCanAlso: "The assistant can also search the web and read links you paste automatically.",
      downloadConversation: "Download conversation",
      toSend: "to send",
      forNewLine: "for a new line",
      stopGenerating: "Stop generating",
      creatingConversation: "Creating conversation",
      sendMessage: "Send message",
      eyebrow: "AI topic builder",
      headline: "What should we practise today?",
      lede: "Describe a theme and I'll draft the questions, the vocabulary, and a speaking guide — then we can start talking out loud right away.",
      startWithThese: "Start with one of these",
      native: "Native: {{language}}",
      interests: "Interests: {{list}}",
      goal: "Goal: {{goal}}",
      searchesWeb: "Searches the web",
      readsLinks: "Reads links you paste",
      acceptsFiles: "Accepts PDF & TXT",
      suggestions: {
        dailyRoutines: "Daily routines",
        dailyRoutinesDesc: "Habits, schedules, and everyday life",
        travelPlans: "Travel plans",
        travelPlansDesc: "Trips, bookings, and new places",
        foodCooking: "Food & cooking",
        foodCookingDesc: "Recipes, restaurants, and tastes",
        jobInterviews: "Job interviews",
        jobInterviewsDesc: "Careers, goals, first impressions",
        moviesTv: "Movies & TV",
        moviesTvDesc: "Shows, genres, recommendations",
        technology: "Technology",
        technologyDesc: "Gadgets, apps, and the future",
      },
    },
    aiPreferences: {
      ariaLabel: "AI preferences",
      heading: "What the AI knows about you",
      subtitle: "Learned automatically from your chats — edit them here anytime.",
      loading: "Loading preferences…",
      englishLevel: "English level",
      selectLevel: "Select level",
      nativeLanguage: "Native language",
      nativeLanguagePlaceholder: "e.g. Vietnamese, Spanish",
      interests: "Interests",
      interestsLabel: "Interests (comma separated)",
      interestsPlaceholder: "e.g. cooking, travel, tech",
      practiceGoal: "Practice goal",
      practiceGoalPlaceholder: "e.g. pass IELTS 7.0",
      save: "Save",
      clear: "Clear",
      edit: "Edit",
      emptyState: "Nothing saved yet. Tell the AI your level, native language or interests — it will remember them here.",
      setManually: "Set preferences manually",
      recentLearning: "Recent learning",
      learnedFromChat: "Learned from chat",
      setManuallyShort: "Set manually",
      autoLearned: "Auto-learned",
      autoLearnedAt: "Auto-learned · {{time}}",
      goal: "Goal",
      secondsAgo: "{{count}}s ago",
      minutesAgo: "{{count}}m ago",
      hoursAgo: "{{count}}h ago",
      daysAgo: "{{count}}d ago",
      saved: "Preferences saved",
      savedDescription: "The AI will use these in future chats.",
      savedDescriptionAgain: "Saved. The AI will use these in future chats.",
      saveFailed: "Could not save preferences. Please try again.",
      cleared: "Preferences cleared",
      clearedDescription: "The AI will re-learn your level and interests from your next chats.",
      clearFailed: "Could not clear preferences. Please try again.",
    },
    generatingView: {
      generatingQuestions: "Generating Questions",
      generatingVocabulary: "Generating Vocabulary",
      generatingSpeakingGuide: "Generating Speaking Guide",
      generatingYourTopic: "Generating Your Topic",
      finishingUp: "Finishing up…",
      warmingUp: "Warming up…",
      cancelGeneration: "Cancel generation",
      ready: "Ready",
      queued: "Queued",
      starting: "Starting…",
      working: "Working…",
      questionsReady: "questions ready",
      wordsFound: "words found",
      guideComplete: "guide complete",
    },
    bannerDialog: {
      title: "Choose a Banner",
      subtitle: "Search free-licence photos or upload your own image.",
      searchTab: "Photo search",
      uploadTab: "Upload",
      searchPlaceholder: 'Search for "{{topic}}"...',
      searchFailed: "Search failed",
      noResults: "No images found. Try a different search.",
      selected: "Selected:",
      useThisImage: "Use This Image",
      clickToBrowse: "Click to browse",
      uploadHint: "JPG, PNG, GIF, WebP — max 5MB",
      previewAlt: "Preview",
      willReplace: "This will replace the existing banner.",
      clear: "Clear",
      tryAgain: "Try again",
      searchUnavailable: "Image search is unavailable right now. Please try again.",
      invalidFileType: "That is not an image we can use. Choose a JPG, PNG, GIF or WebP.",
      fileTooLarge: "That image is larger than 5MB. Choose a smaller file.",
      attributionPrefix: "Photo by",
    },
    detail: {
      notFound: "Topic not found or access denied",
      goBack: "Go back to My Topics",
      shared: "Shared",
      sharedToGroup: "Topic shared to group.",
      removed: "Removed",
      removedFromGroup: "Topic removed from group.",
      failedToShare: "Failed to share.",
      failedToRemove: "Failed to remove.",
      madePublicTitle: "Session Made Public!",
      madePublicDescription: "Session is now visible to everyone",
      failedToMakePublic: "Failed to make session public",
      linkCopiedTitle: "Link Copied!",
      linkCopiedDescription: "Share this link with others",
      categoryUpdated: "Category Updated",
      categoryChanged: "Category changed to {{category}}",
      failedToUpdateCategory: "Failed to update category",
      pinnedTitle: "Session Pinned",
      unpinnedTitle: "Session Unpinned",
      pinnedDescription: "This session will appear in your pinned list",
      unpinnedDescription: "Session removed from pinned list",
      alwaysPublicTitle: "Topics are always public",
      alwaysPublicDescription: "Sharing is enabled by default so everyone can learn from your topics.",
      madePublicToast: "Made Public",
      failedToUpdateSession: "Failed to update session",
      sessionDeleted: "Session Deleted",
      sessionDeletedDescription: "Practice session has been removed",
      failedToDeleteSession: "Failed to delete session",
      bannerSaved: "Banner Saved",
      bannerReplaced: "Banner has been replaced.",
      bannerUploaded: "Banner has been uploaded.",
      unsplashBannerSet: "Unsplash banner has been set.",
      uploadFailed: "Upload Failed",
      failedToUploadBanner: "Failed to upload banner.",
      failedToSaveBannerTitle: "Failed to Save Banner",
      failedToSaveBanner: "Failed to save banner.",
      bannerRemoved: "Banner Removed",
      bannerRemovedDescription: "Topic banner has been removed",
      failedToRemoveBanner: "Failed to remove banner",
    },
  },
  speak: {
    idle: "Listen",
    speaking: "Stop",
  },
  notifications: {
    dismiss: "Dismiss notification",
    header: "Notifications",
    markAllRead: "Mark all read",
    loading: "Loading notifications...",
    noNotifications: "No notifications",
    noTabNotifications: "No {{tab}} notifications",
    emptyAll: "You'll see notifications here when something happens in your study groups",
    emptyUpdates: "Check back soon for product updates and announcements",
    emptyOther: "Try checking other tabs for more notifications",
    deleteTitle: "Delete Notification",
    deleteDescription: "Are you sure you want to delete this notification? This action cannot be undone.",
    deleteConfirm: "Delete",
    tabs: {
      all: "All",
      activity: "Activity",
      social: "Social",
      invites: "Invites",
      updates: "Updates",
    },
    categories: {
      feature: "New Feature",
      improvement: "Improvement",
      fix: "Bug Fix",
      announcement: "Announcement",
    },
  },
  topicTabs: {
    all: "All",
    recent: "Recent",
    trending: "Trending",
    popular: "Popular",
    // "Most Bookmarked" was 391px in a 372px strip at 390 and the last pill
    // clipped mid-word. The tab is the short form; the page header keeps the
    // full name.
    mostBookmarked: "Bookmarked",
  },
  videos: {
    title: "Video Learning",
    resultsHeading: "Video results",
    description: "Learn English with authentic videos from YouTube, TikTok, Instagram & more",
    eyebrow: "Watch & shadow",
    metaLabel: "Interactive Captions",
    metaDescription: "Click any caption word to translate, save it to your vocabulary, or jump to that moment in the video.",
    metaLabelTwo: "Shadowing Practice",
    metaDescriptionTwo: "Record yourself repeating captions to improve pronunciation, rhythm, and fluency.",
    libraryLabel: "Library",
    videoLessons: "Video lessons",
    videosLabel: "Video lessons",
    videoSingular: "video",
    videoPlural: "videos",
    categorySingular: "category",
    categoryPlural: "categories",
    searchPlaceholder: "Search videos...",
    clearSearch: "Clear search",
    certLevelAria: "Filter by CEFR level",
    requestVideo: "Request a Video",
    browseTopics: "Browse Topics",
    suggestHint: "Don't see what you want? Suggest a video and we'll add it.",
    addedThisWeek: "Added this week",
    seoTitle: "Video Learning | Lyder Talk",
    seoDescription: "Learn English by watching videos with interactive captions and shadowing practice",
    videoStatsLabel: "Video library statistics",
    inContext: {
      entry: "Search in context",
      title: "In context",
      description: "Type a word or phrase and hear it said in the videos you already learn from. Every hit is a real moment in a real video, not a definition.",
      eyebrow: "Watch & shadow",
      backToLibrary: "Back to library",
      searchPlaceholder: "Type a word or phrase…",
      searchLabel: "Search video captions",
      searchAction: "Search",
      clearSearch: "Clear search",
      sourceLabel: "Search in",
      sourceAll: "All videos",
      sourceLibrary: "Library",
      sourceImported: "Imported",
      transport: {
        clipGroup: "Clip",
        playbackGroup: "Playback",
        previous: "Previous clip",
        next: "Next clip",
        back: "Back 5 seconds",
        forward: "Forward 5 seconds",
        play: "Play",
        pause: "Pause",
        replay: "Say it again",
        loop: "Repeat this clip",
        speed: "Playback speed",
      },
      prompt: "Type at least {{min}} characters to search every caption line in the library.",
      searching: "Searching captions…",
      error: "Caption search is unavailable right now. Try again in a moment.",
      noResults: "No clip in the library says that yet.",
      noResultsHint: "Try one common word on its own — the search matches whole words, so a phrase has to appear exactly as spoken.",
      videoSingular: "video",
      videoPlural: "videos",
      openVideo: "Open full lesson",
      loadMore: "Show more clips",
      opensOn: "Opens on {{platform}} — this app cannot jump to the moment there",
      seoTitle: "In Context | Lyder Talk",
      seoDescription: "Hear any word or phrase said in real videos from the library, at the exact moment it is spoken.",
      wordDialog: {
        description: "Hear it said in real videos, at the exact moment the word is spoken. Slow it down and shadow the line.",
        empty: "No clip in the library says this word yet.",
        allClips: "Open in In context for every clip",
      },
    },
  },
  leaderboard: {
    title: "Leaderboard",
    description: "See how your practice stacks up against other learners.",
    eyebrow: "Community",
    highlights: "Leaderboard highlights",
    topRanking: "Top Ranking",
    topRankingDescription: "Best overall performers",
    topStudyTime: "Top Study Time",
    topStudyTimeDescription: "Most active practice time",
    topVocabulary: "Top Reviewed Vocabulary",
    topVocabularyDescription: "Most reviewed vocabulary",
    topLevels: "Top Levels",
    highestLevels: "Highest levels",
    topStreaks: "Top Streaks",
    topStreaksDesc: "Longest learning streaks",
    noStreaks: "No streaks yet",
    streakHint: "Practice daily to build your streak!",
    noLevels: "No levels yet",
    levelsHint: "Start earning XP to level up!",
    startVocab: "Start learning vocabulary!",
    longestStreak: "Longest streak",
    mostTime: "Most time",
    topVocabularyShort: "Top vocabulary",
    topXp: "Top XP",
    level: "level",
    days: "days",
    reviews: "reviews",
    rankings: "Rankings",
    rankingsDesc: "See where you rank among all learners this week, this month, and all-time.",
    streaks: "Streaks",
    streaksDesc: "Consecutive days of practice — keep the chain going to build consistency.",
    achievements: "Achievements",
    achievementsDesc: "Unlock badges and milestones as you reach new learning goals.",
    heroDesc: "Recognizing our most dedicated learners and their achievements.",
    seoTitle: "Leaderboard - Top Learners & Achievements",
    seoDesc: "Discover the top learners ranked by study time, XP level, and learning streaks.",
    noData: "No data",
    noDataDescription: "Check back later!",
    noRankings: "No rankings yet",
    noRankingsDescription: "Start learning to appear here!",
    noLearners: "No learners yet",
    noLearnersDescription: "Start practicing to appear here!",
    noVocabulary: "No vocabulary yet",
    noVocabularyDescription: "Save and review some words to appear here!",
    overall: "overall",
    levelLabel: "Level",
    timeLabel: "Time",
    streakLabel: "Streak",
    reviewsUnit: "reviews",
    wordsUnit: "words",
    topicsUnit_one: "topic",
    topicsUnit_other: "topics",
    yourRank: "Your Rank",
    compete: "Compete",
    claimRank: "Finish a session to claim your first rank",
    signInRank: "Sign in to track your rank",
    studyTimeTitle: "Study Time",
    vocabularyTitle: "Vocabulary",
    streaksTitle: "Streaks",
    levelsTitle: "Levels",
    fullRankings: "Full leaderboard rankings",
    levelShort: "Lv. {{level}}",
    periodsLabel: "Ranking period",
    periodToday: "Today",
    periodWeek: "This week",
    periodMonth: "This month",
    periodAll: "All time",
    periodPhraseToday: "today",
    periodPhraseWeek: "this week",
    periodPhraseMonth: "this month",
    // "All time" has no phrase to slot into a sentence, and every surface that
    // would use one branches to its own "all" copy instead. It still needs a
    // real value: an empty string reads as a missing word to the i18n guard.
    periodPhraseAll: "all time",
    metricsLabel: "Ranking category",
    metricAll: "All",
    metricXp: "XP",
    metricTime: "Study time",
    metricVocab: "Vocabulary",
    metricStreak: "Streak",
    rankedByXp: "Ranked by total XP",
    rankedByXpPeriod: "Ranked by XP earned {{period}}",
    rankedByTime: "Ranked by total practice time",
    rankedByTimePeriod: "Ranked by practice time {{period}}",
    rankedByVocab: "Ranked by vocabulary reviews",
    rankedByVocabPeriod: "Ranked by words added {{period}}",
    rankedByStreak: "Ranked by longest streak",
    rankedByStreakPeriod: "Ranked by days practised {{period}}",
    unitXp: "XP",
    unitWord_one: "word",
    unitWord_other: "words",
    unitReview_one: "review",
    unitReview_other: "reviews",
    unitDay_one: "day",
    unitDay_other: "days",
    learnersRanked_one: "{{count}} learner ranked",
    learnersRanked_other: "{{count}} learners ranked",
    allBoardsSubtitle: "Every board at a glance",
    popularTopics: "Popular topics",
    popularTopicsSubtitle: "The topics learners open, like and comment on most.",
    viewFullBoard: "See the full board",
    yourRankByXp: "Your rank by XP",
    yourRankByXpPeriod: "Your rank by XP {{period}}",
    yourPosition: "Your position",
    yourRankPeriod: "Your rank {{period}}",
    you: "You",
    showMoreLearners: "Show more learners",
    allTimeOnlyNote: "Period rankings need the latest database update — showing all-time results for now.",
    emptyPeriodDescription: "Nobody has scored {{period}} yet.",
    emptyHint: "Finish a session to put yourself on the board.",
    loadError: "Couldn't load the leaderboard",
    loadErrorHint: "Check your connection and try again in a moment.",
    streakDays_one: "{{count}} day",
    streakDays_other: "{{count}} days",
  },
  studyGroups: {
    title: "Study Groups",
    description: "Practise the same material with other learners. Groups share practice topics and vocabulary collections, and pick one to focus on each day.",
    metaLabel: "Shared practice",
    metaDescription: "Everyone works through the same topics, so you can compare answers and spot what you missed.",
    metaLabelTwo: "Collective vocabulary",
    metaDescriptionTwo: "Group vocabulary collections turn scattered word lists into one shared deck to drill.",
    clearSearch: "Clear search",
    signInTitle: "Sign in to Access Your Groups",
    signInDescription: "Create and manage your own study groups, collaborate with other learners, and track your progress.",
    joinSignInTitle: "Sign in to join this group",
    joinSignInDescription: "Study groups need an account so the group knows who joined.",
    leaveTitle: "Leave Group",
    leaveDescription: "Are you sure you want to leave this group? You will need to be invited or join again to rejoin.",
    openGroups: "Open groups",
    myGroups: "My groups",
    youBelongTo: "you belong to",
    members: "Members",
    activeToday_other: "{{count}} active today",
    studyGroupStatsLabel: "Study group statistics",
    noDescription: "No description yet — open the group to see what members are sharing.",
    groupPublic: "Public",
    groupPrivate: "Private",
    groupOwner: "Owner",
    groupAdmin: "Admin",
    groupMember: "Member",
    groupFull: "Full",
    groupSeatsOpen: "{{count}} seat(s) open",
    groupSeatsSr: "members, seats available",
    groupFullSr: "This group is full",
    createGroup: "Create Group",
    myGroupsTab: "My Groups",
    allGroupsTab: "All Groups",
    noneJoinedTitle: "You haven’t joined any groups yet",
    noneJoinedDescription: "Start your own and share practice topics with classmates, or join an open group from the All Groups tab.",
    createFirst: "Create your first group",
    noMatchTitle: "No groups match your search",
    noMatchDescription: "Try a different name, description, or member — or browse everything that’s open.",
    showingFound: "Showing {{shown}} of {{total}} groups",
    showingMine: "Showing {{count}} groups you’ve joined",
    showingAll: "Showing {{count}} groups",
    share: "Share",
    shareCollections: "Share {{count}} collections",
    shareTopics: "Share {{count}} topics",
    shareCollectionTitle: "Share Collection",
    shareCollectionDescription: "Choose vocabulary collections to share with your group",
    noCollectionsFound: "No collections found",
    noCollectionsYet: "No collections yet",
    noPublicCollections: "No public collections",
    createCollectionFirst: "Create a vocabulary collection first to share it with the group",
    noPublicCollectionsFromOthers: "There are no public collections from other users yet",
    myTopicsTab: "My Topics",
    communityTab: "Community",
    shareSessionTitle: "Share Session",
    shareSessionDescription: "Choose topics to share with your group",
    searchPlaceholder: "Search groups by name, topic or member…",
    avatarUploadFailed: "Group created but avatar upload failed",
    createFailed: "Failed to create study group",
    createError: "An error occurred while creating the group",
    joinFailed: "Failed to join group",
    leftSuccess: "Left the group successfully",
    leaveFailed: "Failed to leave group",
    deleteSuccess: "Group deleted successfully",
    deleteFailed: "Failed to delete group",
    deleteTitle: "Delete Group",
    deleteConfirm: "Are you sure you want to delete this group? This action cannot be undone. All members, shared sessions, and related data will be permanently removed.",
    noPublicGroups: "No public groups yet",
    noPublicGroupsDesc: "Nobody has opened a group to the community. Be the first.",
    // Study group detail + card action labels (previously hardcoded English)
    join: "Join",
    manage: "Manage",
    leave: "Leave",
    leaveGroup: "Leave group",
    dialogDeleteSharedSessionTitle: "Delete Shared Session",
    dialogDeleteSharedSessionDescription: "Are you sure you want to remove this shared session from the group? This action cannot be undone.",
    dialogRemoveSharedCollectionTitle: "Remove Shared Collection",
    dialogRemoveSharedCollectionDescription: "Are you sure you want to remove this shared collection from the group? This action cannot be undone.",
    dialogTypeToConfirm: "Type {{name}} to confirm",
    dialogEnterGroupName: "Enter group name",
    dialogGroupNameMismatch: "Group name does not match",
    dialogDeleting: "Deleting...",
    dialogRemovingMemberTitle: "Remove Member",
    dialogRemovingMemberDescription: "Are you sure you want to remove {{name}} from this group? This action cannot be undone.",
    dialogRemoveMemberConfirm: "Remove Member",
    clearFilters: "Clear filters",
    shareTopic: "Share a Topic",
    shareAndSet: "Share & Set",
    setTopicOfDay: "Set topic of the day",
    shareCollection: "Share a Collection",
    full: "Full",
    moreActionsFor: "More actions for {{name}}",
    membersAndInvites: "Members & invites",
    editGroup: "Edit group",
    deleteGroup: "Delete group",
    startedBy: "Started by {{name}}",
    noDescriptionDetail: "No description yet — the owner hasn’t said what this group studies.",
    joinGroup: "Join Group",
    joinGroupToView: "Join the group to view sessions and collections",
    groupFullLimit: "{{name}} has reached its limit of {{max}} members, so it isn’t accepting new ones right now.",
    groupOpenMembership: "{{count}} of {{max}} members have joined. Membership is open to anyone.",
    groupInviteOnly: "{{name}} is invite-only. Ask a member to send you an invitation to see what they’re studying.",
    moreGroupActions: "More group actions",
    membersCount: "Members ({{count}})",
    sharedPracticeTopics: "Shared practice topics",
    sharedVocabularyCollections: "Shared vocabulary collections",
    tabTopics: "Topics",
    tabCollections: "Collections",
    sharedMaterial: "Shared material",
    searchTopics: "Search topics…",
    searchCollections: "Search collections…",
    newest: "Newest",
    mostViewed: "Most Viewed",
    mostLiked: "Most Liked",
    showingCount: "Showing {{shown}} of {{total}}",
    todayFocus: "Today’s Focus",
    pickTopicTogether: "Pick what the group studies together",
    noTopicToday: "No topic set for today",
    selectTopicHelp: "Select a topic to help the group focus their practice",
    practiceCalendar: "Practice Calendar",
    recentActivity: "Recent Activity",
    todaysPlans: "Today’s Plans",
    noPlansForDay: "No plans for this day — click + to set one",
    noPracticePlans: "No practice plans for this day",
    clearPlan: "Clear plan",
    calendarToday: "Today",
    calendarWeek: "Week",
    calendarMonth: "Month",
    plan: "Plan",
    viewAllEvents: "View all {{count}} events",
    currentTopicOfDay: "Topic of the Day",
    setAsTopicOfDay: "Set as Topic of the Day",
  },
  session: {
    breadcrumbTopics: "Topics",
    questions: "Questions",
    inContext: {
      title: "Hear these words in context",
      description: "Real clips from the video library where this topic's words are actually spoken.",
      action: "Find clips",
      hide: "Hide",
      watchWord: "Watch",
      watchPhrase: "Watch: hear it in a video",
      found: "{{matched}} of {{total}} words found in videos",
      loading: "Looking through the captions…",
      error: "Caption search is unavailable right now. Try again in a moment.",
      empty: "None of these words have been said in the library yet.",
      emptyHint: "Caption search matches whole words, so a newly generated word appears once a matching video is added.",
      truncated: "Showing the first {{count}} words of this topic. Search any word below for the rest.",
      allClips: "Search any word in the library",
    },
    allQuestions: "All Questions",
    closeQuestionList: "Close question list",
    vocabulary: "Vocabulary",
    reference: "Reference",
    discussion: "Discussion",
    loadingDiscussion: "Loading Discussion...",
    speakingGuide: "Speaking Guide",
    guideExpressions: "Expressions",
    guideStarters: "Starters",
    guideFillers: "Fillers",
    guideGrammar: "Grammar",
    noContentAvailable: "No content available",
    viewNote: "View note",
    like: "Like",
    unlike: "Unlike",
    bookmark: "Bookmark",
    bookmarked: "Bookmarked",
    removeBookmark: "Remove bookmark",
    edit: "Edit",
    share: "Share",
    moreActions: "More actions",
    rteBold: "Bold",
    rteItalic: "Italic",
    rteUnderline: "Underline",
    rteStrikethrough: "Strikethrough",
    rteInlineCode: "Inline Code",
    rteBulletList: "Bullet List",
    rteNumberedList: "Numbered List",
    rteQuote: "Quote",
    rteAddLink: "Add Link",
    rteAddImage: "Add image",
    rteImageUploading: "Uploading image…",
    rteImageAlt: "Attached image",
    rteImageRejected: "That image can't be added",
    rteImageTooLarge: "Images must be 5MB or smaller.",
    rteImageWrongType: "Use a JPEG, PNG, GIF or WebP image.",
    rteImageEmpty: "That file is empty.",
    rteImageFailed: "Couldn't upload that image",
    rteImageFailedHint: "Check your connection and try again.",
    rteMention: "Mention a question, word or expression",
    mentionPickerTitle: "Mention from this topic",
    mentionEmpty: "Nothing matches “{{query}}”",
    mentionQuestionsGroup: "Questions",
    mentionVocabularyGroup: "Vocabulary",
    mentionExpressionsGroup: "Expressions",
    mentionHereNow: "Here now",
    mentionNavigate: "to move",
    mentionInsertHint: "to add",
    mentionOpen: "Open “{{label}}”",
    viewImage: "View full image",
    imageViewerTitle: "Image preview",
    rteSubmit: "Submit",
    liveChat: "Live chat",
    openLiveChat: "Open live chat",
    closeLiveChat: "Close live chat",
    hereCount: "{{count}} here",
    chatToday: "Today",
    chatYesterday: "Yesterday",
    chatEmptyTitle: "No messages yet",
    chatEmptyHint: "Say hello — this chat is saved with the topic, so the next learner will see it.",
    chatLoadingHistory: "Loading messages…",
    chatLoadEarlier: "Load earlier messages",
    chatLoadingEarlier: "Loading…",
    chatPlaceholder: "Say something… (@ to mention)",
    chatMention: "Mention someone or something",
    discussionPlaceholder: "Add comment… (@ to mention)",
    chatEmoji: "Add emoji",
    chatEmojiSearch: "Search emoji",
    chatSend: "Send message",
    chatSignIn: "Sign in to join the chat",
    chatConnecting: "Connecting…",
    chatSending: "Sending…",
    chatFailed: "Not sent",
    chatYou: "You",
    chatLearner: "Learner",
    chatUnread: "{{count}} unread",
    expandChat: "Expand chat",
    collapseChat: "Collapse chat",
    dragToMove: "Drag to move",
    resetChatPosition: "Reset chat position",
    saveToVocabulary: "Save to vocabulary",
    removeFromSaved: "Remove from saved",
    noDiscussionYet: "No discussion yet",
    beFirstToShare: "Be the first to share your thoughts!",
    comments: "Comments",
    practiceWithAi: "Practice with AI",
    aiDescription: "Have a conversation about this topic with an AI tutor",
    startPractice: "Start Practice",
    chooseMode: "Choose Mode",
    viewTopic: "View Topic",
    questionsVocabAtAGlance: "Questions & vocabulary at a glance",
    speakWithAiFeedback: "Speak with AI feedback in real time",
    practiceVocabulary: "Practice Vocabulary",
    vocabularyDescription: "Review and memorize all {{count}} words from this topic",
    studyWords: "Study Words",
    letsGetStarted: "Let's get started!",
    expandStudyGuide: "Expand study guide",
    collapseStudyGuide: "Collapse study guide",
    /** `QuestionCard` — the one-line "what's inside the guide" hint strip. */
    guideSteps_one: "{{count}} step",
    guideSteps_other: "{{count}} steps",
    guideExampleHint: "Example answer",
    guideKeyPointsHint_one: "{{count}} key point",
    guideKeyPointsHint_other: "{{count}} key points",
    /** `SidebarVocabRow` — the label above a word's example sentence. */
    exampleLabel: "Example",    greatStart: "Great start!",
    keepItUp: "Keep it up!",
    almostThere: "Almost there!",
    soClose: "So close!",
    wellDone: "Well done!",
    done: "Done",
    words: "Words",
    time: "Time",
    completed: "Completed",
    markComplete: "Mark complete",
    expand: "Expand",
    collapse: "Collapse",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    reset: "Reset",
    moveSidebarLeft: "Move sidebar to left",
    moveSidebarRight: "Move sidebar to right",
    collapseSidebar: "Collapse sidebar",
    showSidebar: "Show sidebar",
    byAuthor: "By {{name}}",
    learnersHere_one: "{{count}} learner here",
    learnersHere_other: "{{count}} learners here",
    peersCompleted_one: "{{count}} other learner completed this",
    peersCompleted_other: "{{count}} other learners completed this",
    daysAgoShort: "{{count}}d ago",
    hoursAgoShort: "{{count}}h ago",
    minutesAgoShort: "{{count}}m ago",
    questionsPracticed: "questions",
    vocabulariesReviewed: "vocabularies",
    studied: "studied",
    /** `/session/:slug/practice` — the immersive practice chrome. */
    breadcrumbPractice: "Practice",
    toggleSpeakingGuide: "Toggle speaking guide (⌘B)",
    resetProgress: "Reset progress",
    toggleAiChat: "Toggle AI chat (⌘/)",
    aiAssistant: "AI Assistant",
    /** The sticky shortcut hint. Glyphs stay literal; only the prose moves. */
    navHintOr: "or",
    navHintNavigate: "to navigate",
    navHintGuide: "guide",
    navHintChat: "chat",
    /** `StudyGuideSection` (full panel) vs `PracticeStudyGuide` (sidebar rail). */
    guideStepByStepApproach: "Step-by-Step Approach",
    guideExampleAnswer: "Example Answer",
    guideKeyPointsToCover: "Key Points to Cover",
    guideStepByStep: "Step-by-Step",
    guideKeyPoints: "Key Points",
    /** The three switcher tabs on the practice left sidebar. */
    guideTab: "Guide",
    vocabTab: "Vocab",
    speakingTab: "Speaking",
    /** Aria-label for the tablist wrapping those three tabs. */
    studyPanelView: "Study panel view",
    /** Aria-labels on the per-question/per-word completion checkbox. Distinct from
     *  the `markComplete` chip label above — "Mark as complete" for the a11y label. */
    markAsComplete: "Mark as complete",
    markAsIncomplete: "Mark as not complete",
    guideUnavailable: "No study guide available for this question",
    guideUnavailableYet: "No study guide for this question yet",
    /** The practice chat column + its empty state. */
    currentQuestion: "Current Question:",
    /** `ImmersiveQuestionView` — the chips above the question. */
    questionPosition: "Question {{current}} / {{total}}",
    practicedBadge: "Practiced",
    /** `PracticeDictionary` — the docked dictionary widget. */
    openDictionary: "Open Dictionary",
    dictionaryLabel: "Dictionary",
    /** `LargeRecordingInterface` — every state the recorder can sit in. */
    recording: {
      analyzingAnswer: "Analyzing your answer…",
      analyzingHint: "Lydi is listening closely and scoring your speaking.",
      secureTitle: "Recording needs a secure connection",
      secureDescription: "Microphone access only works over HTTPS (or on localhost). Reload the page on a secure URL to record your answer.",
      unsupportedTitle: "Recording isn’t supported here",
      unsupportedDescription: "Your browser can’t access the microphone. Please try a recent version of Chrome, Firefox, Safari, or Edge.",
      micUnreachableTitle: "Can’t reach your microphone",
      micUnreachableHint: "Check that this site is allowed to use your microphone in your browser’s address-bar permissions, then try again.",
      getReady: "Get ready to speak...",
      accessingMicrophone: "Accessing microphone...",
      pausedHint: "Recording paused — click play to continue",
      /** State label beside the live timer while capturing audio. */
      recLabel: "Recording",
      recordAgain: "Record Again",
      /** Sentence-case twin of `recordAgain` — the dialog's confirm button. */
      recordAgainConfirm: "Record again",
      recordAgainTitle: "Record again?",
      recordAgainDescription: "This will start a new recording and replace your current answer and its feedback.",
      keepCurrent: "Keep current",
      analyzingShort: "Analyzing...",
      viewAiFeedback: "View AI Feedback",
      getAiFeedback: "Get AI Feedback",
      tapToStartRecording: "Tap to start recording",
      speakClearly: "Speak clearly and take your time",
      liveTranscription: "Live transcription",
      liveTranscriptionUnavailable: "Live transcription unavailable — we'll transcribe your recording when you finish.",
      liveListening: "Listening...",
    },
    /**
     * `lib/seo.ts`. A non-component module cannot call `useTranslation`, so it
     * reads `i18next` directly — the same thing `formatNextReview` does.
     */
    seo: {
      titleSuffix: "Speaking Practice Session",
      practiceTitlePrefix: "Practice",
      /**
       * `{{categoryText}}` carries its own leading space so the sentence reads
       * `"X" in the Y category.` with a category and `"X".` without one — the
       * same two shapes the hand-written string produced.
       */
      description: "Practice speaking about \"{{topic}}\"{{categoryText}}. Includes {{questionCount}} {{questionNoun}} and {{vocabularyCount}} {{vocabularyNoun}} with definitions and examples. Perfect for language learners looking to improve their conversation skills.",
      categoryIn: " in the {{category}} category",
      questionNoun_one: "practice question",
      questionNoun_other: "practice questions",
      vocabularyNoun_one: "key vocabulary word",
      vocabularyNoun_other: "key vocabulary words",
      resourceType: "Speaking Practice Session",
      educationalLevel: "Beginner to Advanced",
      keywordSpeakingPractice: "speaking practice",
      keywordLanguageLearning: "language learning",
      keywordConversationPractice: "conversation practice",
      keywordEnglishSpeaking: "English speaking",
      keywordAiTutor: "AI language tutor",
    },
    savedTitle: "Saved!",
    savedDescription: "Your words and notes are stored in your library — review them anytime.",
    viewSavedItems: "View saved items",
    streakProtected: "Your streak was protected by a freeze!",
  },
  /**
   * The AI assistant widget, shared by the practice chat column and the
   * floating assistant. Its four starter-prompt chips are deliberately NOT
   * here — they are generation inputs sent to the model, so they stay English
   * (see `ChatMessages`).
   */
  aiAssistant: {
    newConversation: "New Conversation",
    practiceCoach: "Practice Coach",
    learningCoach: "Learning Coach",
    subtitlePractice: "Ask for hints, explanations, vocabulary help, or sample answers.",
    subtitleGeneric: "Your personal language learning companion",
    viewMode: "View mode",
    floating: "Floating",
    sidebar: "Sidebar",
    minimize: "Minimize",
    editingPlaceholder: "Editing message... (Press Escape to cancel)",
    inputPlaceholder: "Ask your learning coach...",
    stopGeneration: "Stop generation",
    signInTitle: "Sign in to chat with Lyder",
    signInBody: "Your learning coach keeps a history and answers about the page you are on. That needs an account.",
  },
  dialogs: {
    close: "Close",
    clearSearch: "Clear search",
    reviewQueue: "Review queue",
    testMicrophone: "Test Microphone",
    selectMicrophone: "Select microphone",
    selectSpeaker: "Select speaker",
    quickPresets: "Quick Presets",
    minutesPlaceholder: "Minutes",
    shareSession: "Share Session",
    shareLink: "Share link",
    share: "Share",
    shareTo: "Share to",
    practiceSession: "Practice session",
    linkCopied: "Link copied",
    linkCopiedHint: "Anyone with the link can open this session.",
    shareImage: "Share image",
    shareImageHint: "4:5 poster · topic, counts and a QR code",
    shareImagePreview: "Preview of the image that will be shared",
    copyImage: "Copy image",
    imageCopied: "Share image copied",
    imageCopiedHint: "Paste it into a chat or a post.",
    downloadQr: "Download",
    imageDownloaded: "Image downloaded",
    imageDownloadedHint: "Ready to share on Zalo or Facebook.",
    posterQuestions: "{{count}} questions",
    posterWords: "{{count}} words",
    visibility: "Visibility",
    maxMembers: "Max Members",
    numberPlaceholder: "Number",
    enterGroupName: "Enter group name",
    addDescription: "Add a description...",
    inviteToGroup: "Invite to Group",
    inviteLinkCopied: "Invite link copied!",
    createInvitation: "Create Invitation",
    addUser: "Add User",
    inviteLinkCopyFailed: "Failed to copy link",
    invitePublicLinkHint: "Start typing to search for users, or enter an email manually. Leave empty for a public invite link.",
    copied: "Copied!",
    copyLink: "Copy Link",
    addingUser: "Adding...",
    creatingInvitation: "Creating...",
    memberOptions: "Member options",
    joinedAgo: "Joined {{time}}",
    searchUserOrEmail: "Search User or Enter Email",
    searchUserPlaceholder: "Type to search users or enter email...",
    inviteLink: "Invite Link",
    searchTopicPlaceholder: "Search by topic or category...",
    setPracticePlan: "Set Practice Plan",
    loadingTopics: "Loading public topics...",
    loadingSessions: "Loading sessions...",
    chooseTopicsDescription: "Choose topics to share with your group",
    shareTopic: "Share Topic",
    shareToGroup: "Share to Study Group",
    noGroupsFound: "No groups found",
    pasteUrl: "Paste YouTube URL (e.g., https://youtube.com/watch?v=...)",
    choosePracticeMode: "Choose Practice Mode",
    comingSoon: "Coming Soon",
    dictation: "Dictation",
    shadowing: "Shadowing",
    noVideosYet: "No videos yet",
    createCollection: "Create Collection",
    collectionTitle: "Collection title",
    collectionDescription: "Collection description",
    noMatches: "No matches",
    loadingCollections: "Loading collections...",
    typeWordPlaceholder: "Type a word and press Enter",
    askCoach: "Ask Coach",
    define: "Define",
    notes: "Notes",
    listen: "Listen",
    stop: "Stop",
    save: "Save",
    saved: "Saved",
    displayNamePlaceholder: "How should others call you?",
    bioPlaceholder: "Tell us a bit about yourself (optional)",
    calendar: "Calendar",
    challenges: "Challenges",
    milestones: "Milestones",
    freezes: "Freezes",
    dialogTabs: "Dialog tabs",
    createGroup: "Create Study Group",
    editGroup: "Edit Study Group",
    createGroupDescription: "Create a new study group to collaborate with other language learners",
    editGroupDescription: "Update your study group settings and information",
    groupName: "Group Name",
    groupNameRequired: "Group name is required",
    groupNameTooShort: "Group name must be at least 3 characters",
    groupNameTooLong: "Group name must be no more than 100 characters",
    charactersCount: "{{count}}/{{max}} characters",
    settings: "Settings",
    public: "Public",
    private: "Private",
    currentMin: "Current: {{count}}. Min {{count}}",
    maxRange: "1–100 max",
    cancel: "Cancel",
    creating: "Creating...",
    updating: "Updating...",
    updateGroup: "Update Group",
    newGroup: "New Group",
    emoji: "Emoji",
    upload: "Upload",
    changeEmoji: "Change Emoji",
    chooseEmoji: "Choose Emoji",
    uploading: "Uploading...",
  },
  review: {
    dailyReview: "Daily review",
    wordsReady: "Words ready",
    videosToRevisit: "Videos to revisit",
    timeToReview: "Time to review",
    dueNow: "Due now",
    words: "Words",
    videos: "Videos",
    estimate: "Est.",
    dueToday: "Due today",
    notPracticed: "Not practiced yet",
    today: "Today",
    yesterday: "Yesterday",
    nothingWaiting: "Nothing is waiting on you right now.",
    moreWaiting: "+{{count}} more words waiting",
    notNow: "Not now — remind me later today",
    reviewWords: "Review {{count}} words",
    practiceVideos: "Practice {{count}} videos",
    listSeparator: " and ",
    queueSummary: "{{items}} are ready. About {{minutes}} minute of practice.",
    daysAgo_other: "{{count}} days ago",
    weekAgo: "1 week ago",
    weeksAgo_other: "{{count}} weeks ago",
    monthAgo: "1 month ago",
    monthsAgo_other: "{{count}} months ago",
    daysLate_other: "{{count}} days late",
  },
  push: {
    unavailable: "Push Notifications Unavailable",
    blocked: "Push Notifications Blocked",
    enable: "Enable Push Notifications",
    unblockHint: "Please unblock in browser settings",
    notified: "Get notified when sessions are shared",
    retrying: "Retrying...",
    retry: "Retry",
    refresh: "Refresh",
    enabling: "Enabling...",
    enableButton: "Enable",
  },
  /** The Statistics pane of the profile dialog. */
  settings: {
    statisticsSubtitle: "Your activity on the platform",
    noStatistics: "No statistics yet",
    noStatisticsHint: "Start practising to see your progress",
    totalLearningTime: "Learning time",
    topicsStudied_one: "{{count}} topic studied",
    topicsStudied_other: "{{count}} topics studied",
    totalSessions: "Sessions",
    publicSessions: "Public",
    studyGroupsShort: "Groups",
    memberSince: "Member since",
    lastActivity: "Last activity",
    streakRecord: "Streak record",
    currentShort: "Now",
    bestShort: "Best",
  },
  rollAndTalk: {
    title: "Roll & Talk",
    eyebrow: "Lotería",
    subtitle: "Draw a card, answer it out loud, and fill a line on your tabla.",
    speakFor: "Speak for",
    speakForSeconds: "Speak for {{seconds}}",
    speakDurationPresets: "Speaking duration presets",
    speakDurationCustom: "Speaking duration in seconds",
    studyGuide: "Study guide",
    drawCard: "Draw a card",
    drawShort: "Draw",
    draw: "draw",
    drawing: "Drawing…",
    drawingStatus: "Drawing a card",
    callingCard: "Calling a card",
    nowCalling: "Now calling",
    roundComplete: "Round complete",
    calledStatus: "{{name}} called",
    cardNumber: "N.º {{number}}",
    yourTabla: "Your tabla",
    tablaProgress: "{{called}} of {{total}} called",
    tablaSpoken: "{{spoken}} answered",
    tablaHint:
      "Fill a row, column or diagonal for ¡Lotería! Tap a called card to hear it again.",
    called: "called",
    spoken: "answered",
    notCalled: "not called",
    mute: "Mute the table",
    unmute: "Unmute the table",
    emptyTitle: "Nothing to deal",
    emptyLevel: "No public questions are set at {{level}} level yet.",
    emptyBody: "No public questions are available yet.",
    emptyHint:
      "Try another level, or browse the topic library and practice a full session.",
    clearFilter: "Clear filter",
    browseTopics: "Browse topics",
    openInPractice: "Open in practice",
    helperDeepLink: "Opens the recording view on this exact question.",
    helperNoSession: "Built-in starter questions have no session to record into.",
    hear: "hear",
    record: "record",
    anyLevel: "Any level",
    filterByLevel: "Filter questions by level",
    win: {
      title: "¡Lotería!",
      topRow: "top row",
      middleRow: "middle row",
      bottomRow: "bottom row",
      leftColumn: "left column",
      middleColumn: "middle column",
      rightColumn: "right column",
      diagonal: "diagonal",
      diagonalReverse: "other diagonal",
      fullTabla: "whole tabla",
      body: "You filled the {{line}} — {{spoken}} of {{total}} cards answered out loud.",
      newTabla: "Deal a new tabla",
    },
    level: {
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",
    },
  },
  /**
   * The product tour (`components/onboarding/`) — the floating tooltip's own
   * chrome plus one `{ title, content }` pair per step.
   *
   * Steps are keyed `<tour>.<stepId>` and listed in the order they appear in
   * `PAGE_TOURS`, so the tooltip copy and the definition that positions it read
   * side by side. `<stepId>` names the element the step points at, never the
   * `data-tour` value itself — that stays English because it is a DOM hook.
   */
  onboarding: {
    /** `TourTooltip` — the chrome that frames a step, not the step's copy. */
    tooltip: {
      closeTour: "Close tour",
      step: "Tour step",
      goToStep: "Go to step {{step}}",
      previousStep: "Previous step",
      nextStep: "Next step",
      finishTour: "Finish tour",
      remindLater: "Remind me later",
      gotIt: "Got it",
    },
    /** `TourHelpButton` — the opt-in replay entry. */
    help: {
      showMeAround: "Show me around",
    },
    tours: {
      home: {
        createTopic: {
          title: "Create a practice topic",
          content: "Click here to create your own practice topic. Type in any subject and AI will generate questions and vocabulary for you to practice.",
        },
        vocabularyWidget: {
          title: "Vocabulary",
          content: "Click here to open your saved words. Use spaced-repetition reviews daily to move words into long-term memory.",
        },
        collectionsWidget: {
          title: "Collections",
          content: "Open vocabulary collections — themed word sets created by you or the community. Browse, save, or create your own to study a topic in depth.",
        },
        practiceWidget: {
          title: "Practice",
          content: "Click here to jump back into your most recently practiced topic, or browse to pick a new one. Aim for at least one session per day to keep your streak alive.",
        },
        streakWidget: {
          title: "Streak & level",
          content: "Click here to see your streak and XP progress. Practice daily to keep your streak alive and level up.",
        },
        leaderboardWidget: {
          title: "Leaderboard",
          content: "Click here to see the global leaderboard. Track your ranking by time studied, XP, streak, and vocabulary reviews.",
        },
        videosWidget: {
          title: "Video practice",
          content: "Click here to browse video lessons. Pick a video to practice shadowing the speaker or typing what you hear (dictation).",
        },
        aiWidget: {
          title: "AI topic creation",
          content: "Click here to chat with the AI and generate new practice topics conversationally. Describe what you want to practice and it suggests topics with questions and vocabulary.",
        },
        studyGroupsWidget: {
          title: "Your study groups",
          content: "Your joined study groups live here in your quick-access widgets. Click any group to view its members, shared topics, and leaderboard. Use 'View All' to browse or create more.",
        },
        featuredCollections: {
          title: "Collections",
          content: "Browse vocabulary collections here — themed word sets curated for popular topics. Click 'View All' to see the full library or create your own.",
        },
        topicOfDay: {
          title: "Topic of the day",
          content: "Every day we pick a featured topic. Click the card to view questions and vocabulary, or hit the practice button to start a speaking session immediately.",
        },
        groupTopicOfDay: {
          title: "Group topic of the day",
          content: "If you're in a study group, this shows the group's current topic. Practice it to contribute to the group leaderboard.",
        },
        allSet: {
          title: "You're all set",
          content: "Pick a topic, practice speaking, and build a daily streak. Use the avatar menu's 'Take a tour' to replay this walkthrough anytime.",
        },
      },
      myTopics: {
        quickGenerate: {
          title: "Quick Generate",
          content: "Click 'Quick Generate' to enter a topic name, pick a category, and immediately generate a full practice set with questions, vocabulary, and optional speaking guide.",
        },
        chatWithAi: {
          title: "Chat with AI",
          content: "Click here to brainstorm topics conversationally with the AI. Describe what you want to practice and it will suggest topics with questions and vocabulary.",
        },
        sidebarHistory: {
          title: "Topic history",
          content: "Every topic you've created or practiced appears here in chronological order. Click any topic to view its questions and vocabulary, or continue practicing. Use 'More' to load older topics.",
        },
        recentTopics: {
          title: "Your recent topics",
          content: "Every topic you generate appears here. Click one to view its questions and vocabulary, or start a practice session. Use the pin icon to keep important topics at the top.",
        },
      },
      myTopicsAi: {
        aiChat: {
          title: "AI topic generation",
          content: "Chat with the AI here to brainstorm and generate practice topics. Describe what you want to practice, and it will suggest topics with questions and vocabulary.",
        },
        messageInput: {
          title: "Type your message",
          content: "Type a topic you want to practice here — for example 'ordering coffee' or 'travel phrases'. Press Enter to send, Shift+Enter for a new line.",
        },
        send: {
          title: "Send your message",
          content: "Click the send button or press Enter to ask the AI to generate a practice topic. Once ready, a preview panel will appear on the right.",
        },
        previewPanel: {
          title: "Review and save",
          content: "When the AI generates a topic draft, it appears here. Review the questions and vocabulary, then click 'Create Topic' to save it to your topics list.",
        },
      },
      myTopicsAiDetail: {
        aiChat: {
          title: "Continue your conversation",
          content: "This is your ongoing AI conversation. Keep chatting to refine your topic, ask for adjustments, or create more practice material.",
        },
        messageInput: {
          title: "Send a follow-up",
          content: "Type a follow-up message to adjust the topic — ask for harder questions, different vocabulary, or a new angle on the same subject.",
        },
        previewPanel: {
          title: "Review drafts",
          content: "Topic drafts from this conversation appear here. Click 'Create Topic' to save a draft, or use 'View' / 'Edit' to open a topic you've already saved.",
        },
      },
      myTopicDetail: {
        practiceTopic: {
          title: "Start practicing",
          content: "Click 'View' to open the practice session for this topic. You'll be able to record yourself answering questions and get instant feedback on fluency and pronunciation.",
        },
        regenerateTopic: {
          title: "Regenerate content",
          content: "Click 'Regenerate' to refresh the questions, vocabulary, and speaking guide for this topic. Useful if you want more practice material or the content feels too easy or hard.",
        },
        pinTopic: {
          title: "Pin for quick access",
          content: "Click the pin icon to keep this topic at the top of your sidebar list. Pinned topics are always easy to find when you want to practice them again.",
        },
        shareTopic: {
          title: "Share with others",
          content: "Click 'Copy Link' to share this topic with friends or classmates. Make it public so anyone can discover and practice it.",
        },
        shareToGroup: {
          title: "Share with your study group",
          content: "Click 'Share to Group' to share this topic with a study group you belong to. Group members can then practice it together and earn points on the group leaderboard.",
        },
        topicBanner: {
          title: "Customize with a banner",
          content: "Click here to add or change the topic banner image. You can upload your own image or search Unsplash to make your topic visually stand out.",
        },
        detailTabs: {
          title: "Browse content tabs",
          content: "Switch between Questions, Vocabulary, and Speaking Guide using these tabs. Each tab shows the content AI generated for this topic.",
        },
        detailContent: {
          title: "Practice content",
          content: "The main content area shows questions, vocabulary words, or speaking tips depending on which tab is active. Click any item to practice or review it.",
        },
      },
      topics: {
        createTopic: {
          title: "Create a topic",
          content: "Click 'Create Topic' to create your own practice topic. Type in any subject and AI will generate questions and vocabulary for you to practice.",
        },
        searchTopics: {
          title: "Search topics",
          content: "Type here to search topics by title. Results update instantly as you type. Click the X to clear your search.",
        },
        filterTabs: {
          title: "Filter by category",
          content: "Use these tabs to switch between All, Recent, Trending, Popular and Bookmarked topics. The category sidebar on the left lets you drill down further.",
        },
        topicsGrid: {
          title: "Pick a topic to practice",
          content: "Click any topic card to view its questions and vocabulary. Use the practice button on each card to jump straight into a speaking session.",
        },
      },
      vocabulary: {
        vocabTabs: {
          title: "Switch between views",
          content: "Use these tabs to switch between 'My Words' and 'Collections'. Collections let you group words into themed sets for focused study.",
        },
        addWord: {
          title: "Add new words",
          content: "Click 'Add Word' to generate new vocabulary with AI. You can enter a word or topic, and it will create definitions, examples, and translations.",
        },
        dailyReview: {
          title: "Daily review reminder",
          content: "When you have words due, this amber card reminds you to review them. The big number is how many are waiting, and each bar below is one topic's share of the queue — click a row, or 'Review everything', to start.",
        },
        searchWords: {
          title: "Search your words",
          content: "Type here to search through your saved words by name or definition. Results update instantly as you type.",
        },
        filterPills: {
          title: "Filter by review status",
          content: "Use these pills to filter your deck by All, Due (words ready for review), Reviewed, No Review, or Incomplete (missing info). Each pill shows the count for that status.",
        },
        stats: {
          title: "Your study stats",
          content: "Track your vocabulary progress here: total saved, due for review, reviewed today, and words needing enrichment.",
        },
        learningPath: {
          title: "Learning path",
          content: "See your vocabulary mastery breakdown across four tiers: New, Learning, Reviewing, and Mastered. Click any tier to filter your deck by that level.",
        },
      },
      vocabularyNotes: {
        notesNew: {
          title: "Create a note",
          content: "Click 'New note' to jot down a free-form personal note — ideas, reminders, or anything you want to remember. It opens in a rich text editor.",
        },
        vocabTabs: {
          title: "Switch between views",
          content: "Use these tabs to move between 'My Words', 'Collections', and 'Notes'. You're on Notes now — your word notes and personal notes live here.",
        },
        notesSearch: {
          title: "Search your notes",
          content: "Type here to search across both word notes and personal notes by keyword. Results update instantly as you type.",
        },
        notesFilters: {
          title: "Filter by type",
          content: "Use these pills to show All notes, just 'Word notes' (attached to vocabulary you studied), or just 'Personal' notes. Each pill shows its count.",
        },
        notesGrid: {
          title: "Your notes",
          content: "All your notes appear here as cards, newest first. Click any card to edit it inline, or use the menu to delete it.",
        },
      },
      vocabularyCollections: {
        createCollection: {
          title: "Create a collection",
          content: "Click 'Create Collection' to build a new themed word set. Add words manually or generate them with AI, then track your mastery as you review.",
        },
        stats: {
          title: "Your stats at a glance",
          content: "See community sets, words available, your collections, and in-progress counts. Use this to track how much vocabulary you're building across all collections.",
        },
        search: {
          title: "Search collections",
          content: "Type here to search collections by title or description. Results update as you type.",
        },
        filterPills: {
          title: "Filter collections",
          content: "Use these pills to filter collections by All, Mine, Public, Shared, or Topics. Each pill shows the count for that category.",
        },
      },
      vocabularyCreateCollection: {
        collectionTitle: {
          title: "Name your collection",
          content: "Give your collection a clear title — like 'Business English' or 'IELTS Prep'. This is how you'll find it later in your collections list.",
        },
        collectionVisibility: {
          title: "Set visibility",
          content: "Public collections appear in the community directory. Private collections are only visible to you. You can change this anytime.",
        },
        collectionWordSource: {
          title: "Choose your word source",
          content: "Pick from your saved vocabulary or type new words to generate definitions with AI. Mix and match both sources in a single collection.",
        },
        collectionWordSearch: {
          title: "Search and select words",
          content: "Search your saved vocabulary by word or definition. Filter by level or type, then check the words you want to include in this collection.",
        },
        collectionInlineInput: {
          title: "Add new words",
          content: "Type any word and press Enter to queue it up. Then click 'Generate' to create definitions, examples, and IPA — all powered by AI.",
        },
        collectionInlineArea: {
          title: "Generate definitions",
          content: "Click 'Generate' to create definitions, examples, and IPA for your queued words. Review the results, check the ones you want, and add them to your collection.",
        },
        collectionYourDeck: {
          title: "Your deck",
          content: "This sidebar shows every word in your collection as cards. You can remove words from here. The count updates live as you add or remove words from either source.",
        },
        collectionAction: {
          title: "Review and save",
          content: "Once you've added words, click 'Review' to check your selection. Then hit 'Create Collection' to save — it'll appear on your collections page.",
        },
      },
      vocabularyCollectionDetail: {
        detailPractice: {
          title: "Start practicing",
          content: "Click 'Practice' to begin reviewing this collection's words in flashcard and quiz mode. Your progress and mastery will update as you study.",
        },
        detailAddWords: {
          title: "Add more words",
          content: "Click 'Add Words' to add more vocabulary to this collection. Pick from your saved words or generate new ones with AI.",
        },
        detailShare: {
          title: "Share this collection",
          content: "Click 'Share' to copy a link to this collection so others can discover and import it. Share it on social media or send it directly to friends.",
        },
        detailShareGroup: {
          title: "Share to a study group",
          content: "Click 'Share to Group' to make this collection available to members of one of your study groups. Everyone in the group can import and practice it.",
        },
        detailEdit: {
          title: "Edit collection",
          content: "Click 'Edit' to change the title, description, visibility, or rearrange the words in this collection.",
        },
        detailDelete: {
          title: "Delete collection",
          content: "Click 'Delete' to permanently remove this collection. This action cannot be undone — all words will be removed from the collection.",
        },
        detailStats: {
          title: "Your progress at a glance",
          content: "Review total words, how many you've reviewed, your mastery percentage, and how many are saved to your library. The mastery bar fills as you study.",
        },
        detailSearch: {
          title: "Search words",
          content: "Search any word, definition, or example in this collection. Results update as you type — a quick way to find specific vocabulary.",
        },
        detailFilterPills: {
          title: "Filter by review status",
          content: "Segment words by All, Unreviewed, Reviewed, or Mastered. Each pill shows the count for that category — use them to focus on what needs attention.",
        },
      },
      vocabularyPractice: {
        loading: "Loading your practice…",
        practiceModeTabs: {
          title: "Choose your practice mode",
          content: "Switch between Flashcards, Writing, Listening, and Word Scramble. Writing combines typing the answer and filling in the blank — each mode exercises a different skill, try them all to build comprehensive understanding.",
        },
        practiceToolbar: {
          title: "Practice controls",
          content: "Reverse the card direction, shuffle the word order, and play audio pronunciation. Use the shortcuts popup to learn keyboard commands for faster practice.",
        },
        practiceCard: {
          title: "Study the card",
          content: "Read the word or definition. Click or press Space to flip the card and reveal the other side. Take your time — accuracy matters more than speed.",
        },
        practiceReview: {
          title: "Rate your recall",
          content: "After flipping, pick one of four difficulty levels: Again (missed it completely), Hard (struggled), Good (got it with some effort), or Easy (instant recall). This trains the spaced repetition algorithm to schedule your next review at the perfect time.",
        },
        practiceSidebar: {
          title: "Track your progress",
          content: "The sidebar shows all words in your practice session. The highlighted one is current; words with a checkmark have been reviewed. Click any word to jump to it.",
        },
      },
      studyGroups: {
        createGroup: {
          title: "Create your own group",
          content: "Click 'Create Group' to start a study group. Set a name, description, and visibility — then invite friends or make it public for anyone to join.",
        },
        searchGroups: {
          title: "Find a group",
          content: "Search for groups by name or description. The results update instantly as you type.",
        },
        tabMy: {
          title: "My Groups",
          content: "Your groups appear here — groups you've joined or created. Click a card to view its members, shared topics, and leaderboard.",
        },
        tabAll: {
          title: "All Groups",
          content: "Browse every public group here. Check the seat count and privacy on each card, then join straight from the card — or open one first to see what its members are studying.",
        },
      },
      leaderboard: {
        metrics: {
          title: "Pick a board",
          content: "Four boards rank the same learners by different numbers: XP earned, practice time, vocabulary, and streak. All shows every board at once; pick one to give it the whole page.",
        },
        periods: {
          title: "Change the window",
          content: "Today, this week, this month or all time. The ranking, the podium and your own position all follow the window you pick.",
        },
        board: {
          title: "Read the board",
          content: "The top three stand on the podium. Everyone below them carries a bar showing their share of the leader's score, and your own position stays pinned at the end if you are further down.",
        },
      },
      videos: {
        requestVideo: {
          title: "Request a video",
          content: "Can't find what you're looking for? Click 'Request a Video' to suggest a topic. New videos are added based on community requests.",
        },
        searchVideos: {
          title: "Search videos",
          content: "Type here to search for videos by title. Results update instantly as you type.",
        },
        levelTabs: {
          title: "Filter by level",
          content: "Use these tabs to filter videos by certification level (A1–C2). Pick the level that matches your current ability.",
        },
        videoList: {
          title: "Pick a video",
          content: "Click any video card to open the practice page. You'll be able to watch the video, then switch between speaking and writing practice modes.",
        },
      },
      videoDetail: {
        videoPlayer: {
          title: "Watch the video",
          content: "Watch the video first to get familiar with the content. Then switch modes below to practice shadowing the captions or typing what you hear (dictation).",
        },
        modeTabs: {
          title: "Choose your practice mode",
          content: "Click 'Speaking' to record yourself shadowing each caption, or 'Dictation' to type what you hear. The counts show how many captions you've practiced in each mode.",
        },
        captionList: {
          title: "Caption list",
          content: "All captions appear here. Click any caption to jump the video to that timestamp. In speaking mode, you can toggle between masked and full-text views.",
        },
      },
      myTopicsTime: {
        timeStats: {
          title: "Your study analytics",
          content: "This shows your total practice time, number of topics, average per topic, and category breakdown. Use the sort buttons to reorder the topic list by Most Time, Recent, or A-Z.",
        },
        sortOptions: {
          title: "Sort your topics",
          content: "Click 'Most Time', 'Recent', or 'A-Z' to reorder your topic list. Use this to find where to focus your practice next.",
        },
      },
      studyGroupDetail: {
        tabTopics: {
          title: "Shared topics",
          content: "Members share practice topics here. Each card shows how many questions and words it holds, so you can judge the size of a topic before opening it. The group's focus for today is flagged.",
        },
        shareButton: {
          title: "Share with the group",
          content: "Sharing is this group's main action. Click Share a Topic to add something you have already built — the label follows the tab you are on, so switch to Collections to share a word list instead.",
        },
        tabCollections: {
          title: "Vocabulary collections",
          content: "Vocabulary collections shared with the group. Each card leads with its word count, and the collection of the day is the one the group agreed to drill. Use the search and category filters above the grid to narrow the list.",
        },
        planButton: {
          title: "Set a practice plan",
          content: "Plan which topics you'll practise on specific days. Members can see who is practising what and when, which makes it easy to arrange a shared study session.",
        },
      },
      sharedSession: {
        sessionTopic: {
          title: "Topic details",
          content: "Here's the topic with its questions and vocabulary. Scroll down to see everything you can practice.",
        },
        sessionStats: {
          title: "Session stats",
          content: "See how many people have viewed this topic, join the comments, and track your study time here too.",
        },
        sessionLike: {
          title: "Like this topic",
          content: "Show appreciation for topics you enjoy. Liking helps others discover quality content.",
        },
        sessionBookmark: {
          title: "Bookmark for later",
          content: "Save this topic to your bookmarks so you can easily find it again. Access your saved topics from the home page.",
        },
        sessionShare: {
          title: "Share with others",
          content: "Share this topic with friends or on social media. Anyone with the link can practice and learn from it.",
        },
        sessionEdit: {
          title: "Edit topic",
          content: "Only you can see this button. Click to edit the topic title, questions, vocabulary, or update the speaking guide.",
        },
        sessionProgress: {
          title: "Your progress",
          content: "Track how many questions you've practiced and vocabulary you've reviewed as you work through the topic.",
        },
        sessionPracticeAi: {
          title: "Practice with AI",
          content: "Have a conversation about this topic with an AI tutor. You'll get real-time feedback on fluency, pronunciation, and vocabulary usage.",
        },
        sessionPracticeVocab: {
          title: "Practice vocabulary",
          content: "Review and memorize all vocabulary words from this topic in flashcard and quiz mode. Master the words before moving on.",
        },
        sessionQuestions: {
          title: "Questions",
          content: "Browse all practice questions for this topic. Use the difficulty filter to focus on Beginner, Intermediate, or Advanced questions. Click a card to reveal the full question, answer, and example response.",
        },
        sessionQuestionNote: {
          title: "Question notes",
          content: "Click the note icon to add a private note to any question. Notes help you remember context, tricky points, or personal connections to the material.",
        },
        sessionVocab: {
          title: "Vocabulary",
          content: "Every word shows its pronunciation, meaning, and an example inline — nothing to open. Filter by CEFR level using the pills above.",
        },
        sessionVocabNote: {
          title: "Add notes",
          content: "Click the note icon to add a private note to any vocabulary word. Notes are saved to your account and help you remember context, usage, or tricky details.",
        },
        sessionVocabSave: {
          title: "Save vocabulary",
          content: "Click the bookmark icon to save a word to your personal vocabulary library. Saved words appear in your vocabulary section for focused review.",
        },
        sessionGuide: {
          title: "Teacher's notes",
          content: "Tap to expand the Speaking Guide — a quick reference with natural expressions, sentence starters, conversation fillers, and grammar tips. Use these to sound more fluent during practice.",
        },
      },
      notifications: {
        bell: {
          title: "Notifications",
          content: "Review reminders, group activity, and other updates show up here. Keep an eye on the bell icon in the top bar.",
        },
      },
      mySpeaking: {
        pageHero: {
          title: "My Speaking",
          content: "Every spoken answer you record across your topics is collected here. Revisit them anytime to hear yourself back and review the AI feedback.",
        },
        topicCard: {
          title: "Your recorded topics",
          content: "Each card shows the topic and your average spoken-score badge. Click a card to expand it and replay every recorded answer with its AI feedback.",
        },
        recordings: {
          title: "Replay and review",
          content: "Expand a topic to see each question, play back your recording, and view the detailed AI feedback. Use 'Practice again' to record a fresh attempt and improve your score.",
        },
      },
      streaks: {
        rewards: {
          title: "Streaks & rewards",
          content: "Keep your daily streak alive and earn XP to level up. Open your avatar menu (top right) anytime to claim rewards and see your stats.",
        },
      },
    },
  },
} as const;
